/**
 * Lead Generation Tab Component
 * AI-powered prompt-based interface for intelligent lead generation
 * Phase 6 - Simplified Prompt-based Search
 */

import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Divider,
  Paper,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  keyframes,
  Autocomplete,
  Popper,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  AutoAwesome as AIIcon,
  LinkedIn as LinkedInIcon,
  Business as BusinessIcon,
  Language as WebIcon,
  Event as EventIcon,
  Email as EmailIcon,
  Send as SendIcon,
  Verified as VerifiedIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import LeadSearchResultCard, { type LeadSearchResult } from '../components/LeadSearchResultCard';
import GeneratedLeadCard from '../components/GeneratedLeadCard';
import LeadDetailModal from '../components/LeadDetailModal';
import { toast } from 'react-toastify';
import { aiCrmApi, apiClient } from '../api/aiCrmApi';
import { useIsMobile } from '../../../utils/responsiveUtils';
import { leadGenerationApi } from '../api/leadGenerationApi';
import type { AddResultToLeadsInput, AISelectedSource, ChatMessage } from '../api/leadGenerationApi';
import type { GeneratedLead, ClarificationState } from '../types';
import AttributeSelectionDialog from '../components/AttributeSelectionDialog';

// Glossy shimmer animation for AI loading
const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const ShimmerChip = ({ width }: { width: number }) => (
  <Box sx={{
    width, height: 28, borderRadius: '16px',
    background: 'linear-gradient(90deg, rgba(237,108,2,0.06) 25%, rgba(237,108,2,0.15) 50%, rgba(237,108,2,0.06) 75%)',
    backgroundSize: '200% 100%',
    animation: `${shimmer} 1.8s ease-in-out infinite`,
    border: '1px solid rgba(237,108,2,0.15)',
  }} />
);

const ShimmerLine = () => (
  <Box sx={{
    height: 16, borderRadius: 1, width: '80%',
    background: 'linear-gradient(90deg, rgba(25,118,210,0.04) 25%, rgba(25,118,210,0.12) 50%, rgba(25,118,210,0.04) 75%)',
    backgroundSize: '200% 100%',
    animation: `${shimmer} 1.8s ease-in-out infinite`,
  }} />
);

// Dropdown suggestions for guided prompt fields
const industrySuggestions = [
  'Fintech', 'Healthcare', 'Real Estate', 'Technology', 'E-commerce',
  'Manufacturing', 'Education', 'Hospitality', 'Logistics', 'Retail',
  'Insurance', 'Legal', 'Media & Entertainment', 'Telecommunications',
  'Energy', 'Automotive', 'Construction', 'Agriculture', 'Food & Beverage',
  'Web3 & AI', 'SaaS', 'Consulting', 'Non-Profit',
];

const locationSuggestions = [
  'Hong Kong', 'Singapore', 'Malaysia', 'Taiwan', 'Japan',
  'Thailand', 'Indonesia', 'Philippines', 'Vietnam', 'South Korea',
  'Australia', 'United Kingdom', 'United States', 'China', 'India',
  'Dubai', 'Germany', 'Canada',
];

const triggerSuggestions = [
  'raised funding', 'expanded to new markets', 'hired new executives',
  'launched a new product', 'opened new offices', 'announced partnerships',
  'gone through digital transformation', 'been acquired or merged',
  'listed on stock exchange', 'won industry awards',
];

// Suggested search prompts by category
const suggestedPrompts = [
  { label: 'Tech Companies in Hong Kong', prompt: 'Technology companies in Hong Kong looking for enterprise software solutions' },
  { label: 'Retail in Singapore', prompt: 'Retail businesses in Singapore seeking digital transformation and e-commerce solutions' },
  { label: 'Manufacturing in Malaysia', prompt: 'Manufacturing companies in Malaysia interested in ERP and supply chain management' },
  { label: 'Healthcare Providers', prompt: 'Healthcare providers and clinics looking for patient management systems' },
  { label: 'Financial Services', prompt: 'Financial services and fintech companies seeking CRM and automation tools' },
  { label: 'Education Sector', prompt: 'Educational institutions and EdTech companies interested in management software' },
  { label: 'Real Estate Agencies', prompt: 'Real estate agencies and property developers looking for CRM solutions' },
  { label: 'Hospitality & Hotels', prompt: 'Hotels and hospitality businesses seeking booking and management systems' },
  { label: 'B2B SaaS Companies', prompt: 'B2B SaaS companies looking to expand their enterprise client base' },
  { label: 'Startups & SMEs', prompt: 'Growing startups and SMEs in Asia Pacific seeking business management tools' }
];

// Demo email templates
const demoEmailTemplates = [
  {
    id: 'demo_intro',
    name: 'Product Demo Introduction',
    subject: 'Schedule a Product Demo - {{companyName}}',
    content: `Dear {{contactName}},

Thank you for your interest in our solutions. I would like to schedule a personalized product demo to show you how we can help {{companyName}} achieve its goals.

During this demo, we will cover:
• Key features tailored to your industry ({{industry}})
• Integration options with your existing systems
• ROI analysis and success stories from similar companies

Please let me know your availability for a 30-minute call, or you can book directly using the link below.

Best regards,
{{senderName}}`
  },
  {
    id: 'demo_followup',
    name: 'Demo Follow-up',
    subject: 'Following Up - Demo Request for {{companyName}}',
    content: `Dear {{contactName}},

I wanted to follow up on my previous message regarding scheduling a product demo for {{companyName}}.

Our solution has helped companies in the {{industry}} industry achieve:
• 40% increase in sales efficiency
• 60% reduction in lead response time
• 25% improvement in conversion rates

Would you be available for a brief 20-minute call this week?

Best regards,
{{senderName}}`
  },
  {
    id: 'demo_value',
    name: 'Value Proposition Demo',
    subject: 'Exclusive Demo Invitation - Transform Your {{industry}} Operations',
    content: `Dear {{contactName}},

I noticed that {{companyName}} is actively growing in the {{industry}} sector. I believe our AI-powered CRM solution could significantly enhance your sales and customer management processes.

I'd love to schedule a demo to show you:
• How our AI scoring can identify your highest-value prospects
• Automated workflows that save 10+ hours per week
• Real-time analytics and reporting dashboards

Let me know when works best for you.

Best regards,
{{senderName}}`
  }
];

export const LeadGenerationTab: React.FC = () => {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  // AI Hints for lead generation
  const { data: hintsData, isLoading: hintsLoading } = useQuery({
    queryKey: ['campaignHints'],
    queryFn: () => leadGenerationApi.getLeadGenerationHints(),
    staleTime: 5 * 60 * 1000,
  });

  // Guided filter fields
  const [filterTitle, setFilterTitle] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterTrigger, setFilterTrigger] = useState('');

  // Auto-build prompt: Find [Title/Department] of [Industry/Sector] located in [Location]
  const buildPromptFromFilters = (title: string, industry: string, location: string, trigger: string) => {
    const parts: string[] = [];
    if (title.trim()) parts.push(`Find ${title.trim()} of`);
    if (industry.trim()) parts.push(`${industry.trim()} sector`);
    if (location.trim()) parts.push(`located in ${location.trim()}`);
    if (trigger.trim()) parts.push(`that have recently ${trigger.trim()}`);
    return parts.join(' ') || '';
  };

  const handleFilterChange = (field: 'title' | 'industry' | 'location' | 'trigger', value: string) => {
    const newTitle = field === 'title' ? value : filterTitle;
    const newIndustry = field === 'industry' ? value : filterIndustry;
    const newLocation = field === 'location' ? value : filterLocation;
    const newTrigger = field === 'trigger' ? value : filterTrigger;
    if (field === 'title') setFilterTitle(value);
    if (field === 'industry') setFilterIndustry(value);
    if (field === 'location') setFilterLocation(value);
    if (field === 'trigger') setFilterTrigger(value);
    setSearchPrompt(buildPromptFromFilters(newTitle, newIndustry, newLocation, newTrigger));
  };

  // Simple prompt-based search state
  const [searchPrompt, setSearchPrompt] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isPollingAI, setIsPollingAI] = useState(false); // Track if AI polling is in progress
  // Track active pipeline jobId so we can issue a best-effort cancel from the Stop Search button
  const currentJobIdRef = useRef<string | null>(null);
  const cancelledJobsRef = useRef<Set<string>>(new Set());
  const [aiProgress, setAiProgress] = useState(0); // Progress percentage (0-100)
  const [aiProgressText, setAiProgressText] = useState(''); // Current stage text
  const [searchResults, setSearchResults] = useState<LeadSearchResult[]>([]);
  const [selectedResults, setSelectedResults] = useState<string[]>([]);
  const [aiSelectedSources, setAiSelectedSources] = useState<AISelectedSource[]>([]);
  const [page, setPage] = useState(1);
  const resultsPerPage = 10;
  const aiResultsQuery = { page: 1, limit: 50, sortBy: 'aiScore', sortOrder: 'desc' };


  // Filter out generic/non-person contacts — must match LeadDetailModal + GeneratedLeadCard
  const genericNames = new Set([
    'general enquiries', 'general enquiry', 'general contact', 'sales team',
    'business development', 'hr department', 'info', 'contact', 'support',
    'customer service', 'reception', 'admin', 'office', 'marketing team',
  ]);
  const isRealPerson = (name: string) => {
    if (!name) return false;
    const lower = name.toLowerCase().trim();
    if (genericNames.has(lower)) return false;
    if (lower.split(/\s+/).length < 2) return false;
    if (/\b(team|department|enquir|contact|support|office|general)\b/i.test(lower)) return false;
    return true;
  };

  // Check if a lead has at least one valid real-person contact
  const hasValidContacts = (lead: GeneratedLead): boolean => {
    // Check metadata.contacts array first
    const meta = lead.metadata as any;
    if (meta?.contacts && Array.isArray(meta.contacts) && meta.contacts.length > 0) {
      if (meta.contacts.some((c: any) => isRealPerson(c.contactName) && c.contactPosition)) return true;
    }
    // Fallback to top-level contact fields
    if (isRealPerson(lead.contactName || '') && lead.contactPosition) return true;
    return false;
  };

  // Filter to leads with valid contacts (for final results)
  const filterEnrichedWithoutContacts = (leads: GeneratedLead[]): GeneratedLead[] => {
    return leads.filter((lead) => {
      if (lead.enrichmentStatus === 'PENDING' || lead.enrichmentStatus === 'IN_PROGRESS') return true;
      return hasValidContacts(lead);
    });
  };

  // New: Generated Leads (compact view with modal)
  const [generatedLeads, setGeneratedLeads] = useState<GeneratedLead[]>([]);
  const [selectedGeneratedLead, setSelectedGeneratedLead] = useState<GeneratedLead | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Search history — grouped by searchPrompt
  interface SearchHistoryItem {
    searchPrompt: string;
    leadCount: number;
    latestDate: string;
    leads: GeneratedLead[];
  }
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([]);
  const [similarSearch, setSimilarSearch] = useState<SearchHistoryItem | null>(null);

  // Time-based eased fake-progress: each stage has a floor, ceiling, and
  // expected duration. Progress eases from floor → ceiling over the expected
  // window using 1 - e^(-t/τ), so the bar moves quickly at first and slows
  // as it approaches the ceiling — feels honest and never appears stuck.
  //
  // On page-resume we seed the timer from the persisted job startedAt so the
  // eased value reflects real elapsed time (otherwise the bar snaps back to 20%).
  const stageStartRef = useRef<{ floor: number; startedAt: number } | null>(null);
  useEffect(() => {
    if (!isPollingAI) return;
    const tick = () => {
      setAiProgress(prev => {
        // Determine current stage from prev value
        let floor: number, ceiling: number, expectedMs: number;
        if (prev < 20)      { floor = 10; ceiling = 19; expectedMs = 30_000; }   // WAITING → SEARCHING (~30s)
        else if (prev < 50) { floor = 20; ceiling = 49; expectedMs = 5 * 60_000; } // SEARCHING (~5 min)
        else if (prev < 70) { floor = 50; ceiling = 69; expectedMs = 3 * 60_000; } // ENRICHING (~3 min)
        else if (prev < 85) { floor = 70; ceiling = 84; expectedMs = 90_000; }    // VERIFYING (~90s)
        else                { floor = 85; ceiling = 94; expectedMs = 60_000; }    // STORING (~60s)

        // First tick of this polling session OR new stage bucket — seed the timer.
        // If the persisted job has been running for a while, use that startedAt
        // so we don't reset elapsed time when the user navigates back.
        if (!stageStartRef.current || stageStartRef.current.floor !== floor) {
          let seedStartedAt = Date.now();
          try {
            const saved = localStorage.getItem('wyni_active_pipeline_job');
            if (saved) {
              const parsed = JSON.parse(saved);
              if (typeof parsed?.startedAt === 'number') seedStartedAt = parsed.startedAt;
            }
          } catch {}
          stageStartRef.current = { floor, startedAt: seedStartedAt };
        }
        const elapsed = Date.now() - stageStartRef.current.startedAt;
        // Eased target: 1 - e^(-t/τ). τ = expectedMs/3 → ~95% of range used by expectedMs.
        const tau = expectedMs / 3;
        const easedFraction = 1 - Math.exp(-elapsed / tau);
        const target = Math.floor(floor + (ceiling - floor) * easedFraction);
        return Math.max(prev, Math.min(target, ceiling));
      });
    };
    const id = setInterval(tick, 1000);
    tick();
    return () => { clearInterval(id); stageStartRef.current = null; };
  }, [isPollingAI]);

  // Rotating sub-status text per stage — keeps the helper line moving even
  // when the % bar slows down approaching a ceiling.
  const SUB_STATUS_BY_STAGE: Record<string, string[]> = {
    waiting:   ['Queuing your search…', 'Reserving capacity…', 'Almost ready to start…'],
    searching: [
      'Searching LinkedIn for matching companies…',
      'Scanning company directories…',
      'Cross-referencing 3+ data sources…',
      'Filtering by your criteria…',
      'Ranking the most relevant companies…',
    ],
    enriching: [
      'Looking up contact details…',
      'Finding decision-makers at each company…',
      'Pulling job titles and seniority…',
      'Enriching with email patterns…',
    ],
    verifying: [
      'Verifying email deliverability…',
      'Checking contact accuracy…',
      'Validating LinkedIn profiles…',
    ],
    storing:   ['Saving leads to your workspace…', 'Finalizing results…'],
  };
  useEffect(() => {
    if (!isPollingAI) return;
    let stageKey = 'searching';
    if (aiProgress < 20) stageKey = 'waiting';
    else if (aiProgress < 50) stageKey = 'searching';
    else if (aiProgress < 70) stageKey = 'enriching';
    else if (aiProgress < 85) stageKey = 'verifying';
    else stageKey = 'storing';
    const messages = SUB_STATUS_BY_STAGE[stageKey];
    let idx = 0;
    setAiProgressText(messages[0]);
    const id = setInterval(() => {
      idx = (idx + 1) % messages.length;
      setAiProgressText(messages[idx]);
    }, 4000);
    return () => clearInterval(id);
    // Re-arm when crossing a stage bucket so the message set matches the stage
  }, [isPollingAI, Math.floor(aiProgress / 20)]);

  // Reusable function to load search history
  const loadSearchHistory = async (showLatestResults = false) => {
    try {
      const result = await leadGenerationApi.searchGeneratedLeads({
        prompt: '',
        page: 1,
        limit: 500,
        includeAIDiscovery: false,
      });
      // Filter out stale PENDING leads (stuck for more than 10 minutes — enrichment failed silently)
      const tenMinAgo = new Date(Date.now() - 10 * 60 * 1000).toISOString();
      const allLeads = (result.results || []).filter((l: any) =>
        (l.enrichmentStatus !== 'PENDING' || l.createdAt > tenMinAgo) &&
        // Exclude leads that were created by a "Find more contacts at <company>" job —
        // those should merge into the original lead's contacts list, not appear as a
        // standalone search-history entry. (Defensive against legacy rows from before
        // the dispatcher routed these results to moreContactsResultsCallback.)
        !/^Find more contacts at /i.test(l.searchPrompt || '')
      );

      // Group by jobId (each search session creates new records with a unique jobId)
      const groups = new Map<string, GeneratedLead[]>();
      for (const lead of allLeads) {
        const meta = lead.metadata as any;
        const jobId = meta?.jobId || meta?.pipeline?.jobId;
        // Use jobId if available, otherwise fall back to searchPrompt + createdAt date
        const key = jobId || `${lead.searchPrompt || 'unknown'}_${(lead.createdAt || '').substring(0, 10)}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(lead);
      }

      // Convert to sorted array (most recent first)
      const history: SearchHistoryItem[] = [];
      for (const [_key, leads] of groups) {
        const prompt = leads[0]?.searchPrompt || 'Unknown search';
        const latestDate = leads.reduce((max, l) =>
          l.updatedAt > max ? l.updatedAt : max, leads[0]?.updatedAt || '');
        history.push({ searchPrompt: prompt, leadCount: leads.length, latestDate, leads });
      }
      history.sort((a, b) => b.latestDate.localeCompare(a.latestDate));

      setSearchHistory(history);

      // On initial load, auto-show the most recent search — including its
      // prompt and filters so "Generate more leads" has context.
      if (showLatestResults && history.length > 0) {
        const latest = history[0];
        setGeneratedLeads(latest.leads);
        setSearchPrompt(latest.searchPrompt);
        const parsed = parsePromptIntoFilters(latest.searchPrompt);
        if (parsed.title) setFilterTitle(parsed.title);
        if (parsed.industry) setFilterIndustry(parsed.industry);
        if (parsed.location) setFilterLocation(parsed.location);
      }
    } catch (err) {
      // Silently fail
    }
  };

  // Reusable: start polling for a pipeline job (used on search start AND page resume)
  const startPipelinePolling = (jobId: string, searchPrompt: string, searchStartedAt: string) => {
    setIsSearching(true);
    setIsPollingAI(true);
    // Floor at 15 — never reduce existing progress (e.g. on page-resume the eased
    // value may already be at 35%, don't snap back to 15).
    setAiProgress(p => Math.max(p, 15));
    setAiProgressText('Finding companies and contacts...');
    // Reset the slow-search banner for this new job
    setSlowSearchBanner({ show: false, dismissed: false, suggestions: [] });
    // Track this job so the Stop Search button can cancel it
    currentJobIdRef.current = jobId;

    const pollInterval = 5000;
    const maxSilentPolls = 60; // Only timeout after 60 polls with no pipeline activity
    let silentPollCount = 0; // Counts polls since last pipeline activity
    let lastCount = 0;
    let pipelineDone = false;
    let lastStatus = '';

    const poll = async () => {
      // If user cancelled this job from the UI, exit silently
      if (cancelledJobsRef.current.has(jobId)) {
        return;
      }
      try {
        let pipelineActive = false;
        try {
          const statusResp = await apiClient.get(`/lead-generation/pipeline-status/${jobId}`);
          const jobStatus = statusResp.data?.data;
          if (jobStatus?.status === 'CANCELLED') {
            // Backend confirmed cancellation — exit silently, no error toast
            cancelledJobsRef.current.add(jobId);
            localStorage.removeItem(PIPELINE_JOB_KEY);
            setAiProgress(0); setIsSearching(false); setIsPollingAI(false);
            if (currentJobIdRef.current === jobId) currentJobIdRef.current = null;
            return;
          }
          if (jobStatus) {
            // Note: server statusMessage intentionally not surfaced — the rotating
            // sub-status text effect provides friendlier per-stage messaging.
            if (jobStatus.status !== lastStatus) { lastStatus = jobStatus.status; }
            // Show the slow-search banner once suggestions arrive during SEARCHING/ENRICHING.
            // Only show it if the user hasn't explicitly dismissed it.
            if (
              Array.isArray(jobStatus.fallbackSuggestions) &&
              jobStatus.fallbackSuggestions.length > 0 &&
              (jobStatus.status === 'SEARCHING' || jobStatus.status === 'ENRICHING')
            ) {
              setSlowSearchBanner(prev =>
                prev.dismissed
                  ? prev
                  : { show: true, dismissed: false, suggestions: jobStatus.fallbackSuggestions }
              );
            }
            // Stage updates only set the floor — never go backwards (fake-tick adds +1% between stages)
            if (jobStatus.status === 'WAITING') { setAiProgress(p => Math.max(p, 10)); pipelineActive = true; }
            else if (jobStatus.status === 'SEARCHING') { setAiProgress(p => Math.max(p, 20)); pipelineActive = true; }
            else if (jobStatus.status === 'ENRICHING') { setAiProgress(p => Math.max(p, 50)); pipelineActive = true; }
            else if (jobStatus.status === 'VERIFYING') { setAiProgress(p => Math.max(p, 70)); pipelineActive = true; }
            else if (jobStatus.status === 'STORING') { setAiProgress(p => Math.max(p, 85)); pipelineActive = true; }
            else if (jobStatus.status === 'DONE') pipelineDone = true;
            else if (jobStatus.status === 'FAILED') {
              const friendlyMsg = formatPipelineError(jobStatus.error);
              const isTimeout = /timed? out|timeout/i.test(jobStatus.error || '');
              toast.error(friendlyMsg);
              setNoLeadsState({
                show: true,
                searchPrompt,
                suggestions: generateNoLeadsSuggestions(searchPrompt),
                reason: isTimeout ? 'timeout' : 'empty',
                aiSuggestedQueries: jobStatus.fallbackSuggestions || [],
              });
              notifyPipelineDone('failed', { searchPrompt, message: friendlyMsg });
              localStorage.removeItem(PIPELINE_JOB_KEY);
              setAiProgress(0); setIsSearching(false); setIsPollingAI(false);
              return;
            }
          }
        } catch {} // Pipeline status is optional

        const searchResult = await leadGenerationApi.searchGeneratedLeads({
          prompt: searchPrompt, page: 1, limit: 30,
          includeAIDiscovery: false, createdAfter: searchStartedAt,
        });
        const leads = searchResult.results || [];
        const completedLeads = leads.filter((l: any) => l.enrichmentStatus === 'COMPLETED');

        if (completedLeads.length > lastCount) {
          setGeneratedLeads(completedLeads);
          lastCount = completedLeads.length;
        }

        // Reset silent counter if pipeline is actively working — never timeout while pipeline is running
        if (pipelineActive) {
          silentPollCount = 0;
        } else {
          silentPollCount++;
        }

        const isDone = pipelineDone || completedLeads.length >= 10;
        const isTimedOut = !pipelineActive && silentPollCount >= maxSilentPolls;

        if (isDone || isTimedOut) {
          if (pipelineDone && completedLeads.length === 0 && silentPollCount < maxSilentPolls) {
            setTimeout(poll, 3000);
            return;
          }
          localStorage.removeItem(PIPELINE_JOB_KEY);
          if (completedLeads.length > 0) {
            setAiProgress(100);
            setAiProgressText('Complete!');
            setGeneratedLeads(completedLeads);
            toast.success(`Found ${completedLeads.length} leads with contacts!`);
            setSearchHistory(prev => {
              const newEntry = { searchPrompt, leadCount: completedLeads.length, latestDate: new Date().toISOString(), leads: completedLeads };
              return [newEntry, ...prev];
            });
            notifyPipelineDone('success', { count: completedLeads.length, searchPrompt });
          } else {
            setNoLeadsState({ show: true, searchPrompt, suggestions: generateNoLeadsSuggestions(searchPrompt), reason: 'empty' });
            toast.info('No leads found. Try a different search.');
            notifyPipelineDone('empty', { searchPrompt });
          }
          setTimeout(() => { setAiProgress(0); setIsSearching(false); setIsPollingAI(false); }, 500);
          return;
        }
        setTimeout(poll, pollInterval);
      } catch (err) {
        silentPollCount++;
        if (silentPollCount < maxSilentPolls) setTimeout(poll, pollInterval);
        else { localStorage.removeItem(PIPELINE_JOB_KEY); setAiProgress(0); setIsSearching(false); setIsPollingAI(false); }
      }
    };
    setTimeout(poll, 3000);
  };

  // Load search history on page mount, and resume any active pipeline job
  useEffect(() => {
    loadSearchHistory(true);

    // Resume active pipeline job if user navigated away and came back.
    // Two-step recovery:
    // 1. Check localStorage for a Lead Generation job — restart full polling
    //    so streaming results re-appear.
    // 2. If no localStorage entry, ask the queue API whether there's any
    //    active LEAD_GEN / FIND_MORE_LEADS job for this user. If yes, just
    //    flip isSearching/isPollingAI so the buttons stay disabled — the
    //    queue tray already shows live progress, and results will appear
    //    on next manual search/refresh.
    const saved = localStorage.getItem(PIPELINE_JOB_KEY);
    if (saved) {
      try {
        const { jobId, searchPrompt, searchStartedAt, startedAt } = JSON.parse(saved);
        if (jobId && searchStartedAt && Date.now() - startedAt < 15 * 60 * 1000) {
          toast.info('Resuming your search in progress...');
          startPipelinePolling(jobId, searchPrompt, searchStartedAt);
          return;
        }
        localStorage.removeItem(PIPELINE_JOB_KEY);
      } catch {
        localStorage.removeItem(PIPELINE_JOB_KEY);
      }
    }

    // Fallback: ask the queue if anything's active for this user
    let cancelled = false;
    const checkQueue = async () => {
      try {
        const resp = await apiClient.get('/lead-generation/queue');
        if (cancelled) return;
        const entries = (resp.data?.data ?? []) as Array<{ kind: string; status: string }>;
        const hasActiveSearch = entries.some(e =>
          (e.kind === 'LEAD_GEN' || e.kind === 'FIND_MORE_LEADS') &&
          (e.status === 'WAITING' || e.status === 'RUNNING')
        );
        if (hasActiveSearch) {
          setIsSearching(true);
          setIsPollingAI(true);
          setAiProgress(p => Math.max(p, 15));
          setAiProgressText('Search in progress — see the queue tray top-right for live status.');
          // Re-check periodically; once queue is clear, release the lock
          setTimeout(checkQueue, 5000);
        } else {
          // Only clear if WE turned them on (don't clobber an active local search)
          setIsSearching(prev => prev && !localStorage.getItem(PIPELINE_JOB_KEY) ? false : prev);
          setIsPollingAI(prev => prev && !localStorage.getItem(PIPELINE_JOB_KEY) ? false : prev);
          setAiProgress(prev => (prev === 15 ? 0 : prev));
        }
      } catch {/* ignore */}
    };
    checkQueue();
    return () => { cancelled = true; };
  }, []);

  // Similar search detection disabled — each search is individual
  /* useEffect(() => {
    if (!searchPrompt.trim() || searchHistory.length === 0) {
      setSimilarSearch(null);
      return;
    }
    const promptLower = searchPrompt.toLowerCase();
    const match = searchHistory.find(h => {
      const hLower = h.searchPrompt.toLowerCase();
      const words = promptLower.split(/\s+/).filter(w => w.length > 3);
      const matchCount = words.filter(w => hLower.includes(w)).length;
      return matchCount >= 2; // At least 2 significant words match
    });
    setSimilarSearch(match || null);
  }, [searchPrompt, searchHistory]); */

  // Load a specific search's results from history
  // Parse a saved search prompt back into its title/industry/location parts
  // so actions like "Generate more leads" have the right context.
  const parsePromptIntoFilters = (prompt: string) => {
    // Handles two formats:
    //   A) Canonical (from buildPromptFromFilters):
    //      "Find <title> of <industry> sector located in <location>[ that have recently <trigger>]"
    //   B) Freeform phrases (AI Hub suggestions):
    //      "Chief Digital Officer Banking HK"
    //      "Head of Digital Banking Hong Kong"
    //      "VP Marketing medical plans Hong Kong"
    const result = { title: '', industry: '', location: '' };
    const KNOWN_LOCATIONS = [
      'Hong Kong', 'HK', 'Singapore', 'SG', 'Malaysia', 'MY', 'Thailand', 'TH',
      'Indonesia', 'ID', 'Philippines', 'PH', 'Vietnam', 'VN', 'Taiwan', 'TW',
      'China', 'CN', 'Japan', 'JP', 'South Korea', 'Korea', 'KR',
      'Australia', 'AU', 'India', 'IN', 'US', 'USA', 'UK', 'EU',
      'Asia Pacific', 'APAC', 'Asia',
    ];

    // ---- Strategy A: canonical format ----
    const canonicalMatch = prompt.match(/^\s*Find\s+(.+?)\s+of\s+(.+?)\s+sector\s+located\s+in\s+(.+?)(?:\s+that\b|\.?\s*$)/i);
    if (canonicalMatch) {
      return {
        title: canonicalMatch[1].trim(),
        industry: canonicalMatch[2].trim(),
        location: canonicalMatch[3].trim(),
      };
    }

    // ---- Strategy B: freeform — detect trailing known location ----
    // Look for a known location at the end of the phrase.
    let working = prompt.trim();
    for (const loc of KNOWN_LOCATIONS) {
      const re = new RegExp(`(?:\\bin\\s+)?${loc.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b\\s*\\.?\\s*$`, 'i');
      const m = working.match(re);
      if (m) {
        result.location = loc;
        working = working.substring(0, m.index).trim().replace(/[,;:\-]+$/, '').trim();
        break;
      }
    }

    // ---- Strategy C: if no known trailing location, fall back to old behaviour ----
    if (!result.location) {
      const locMatch =
        working.match(/located\s+in\s+(.+?)(?:\s+that\b|\.?\s*$)/i) ||
        working.match(/\bin\s+([A-Z][^,\.]+?)(?:\s+that\b|\.?\s*$)/);
      if (locMatch) {
        result.location = locMatch[1].trim();
        working = working.substring(0, locMatch.index).trim().replace(/[,;:\-]+$/, '').trim();
      }
    }

    // ---- Split remaining text into title + industry ----
    // If it contains " of ", split on the FIRST " of " (title = before, industry = after).
    //   "Head of Digital Banking" → title="Head of Digital", industry="Banking" (not perfect,
    //   but title is arguably "Head" and industry is "Digital Banking"). Better to split on
    //   LAST " of " so we capture multi-word titles like "Head of Marketing":
    //   "Head of Marketing of Food Stalls" → title="Head of Marketing", industry="Food Stalls"
    // For AI suggestions without "of" at all (e.g. "Chief Digital Officer Banking HK" →
    // working="Chief Digital Officer Banking"), we can't reliably split, so put everything
    // in industry and let the user adjust if they care.
    if (working) {
      const lastOfIdx = working.search(/\s+of\s+(?=[^]*$)/i);
      // Walk to find the LAST " of "
      let matches: number[] = [];
      const re = /\s+of\s+/gi;
      let m: RegExpExecArray | null;
      while ((m = re.exec(working)) !== null) matches.push(m.index);
      if (matches.length > 0) {
        const lastIdx = matches[matches.length - 1];
        result.title = working.substring(0, lastIdx).trim();
        result.industry = working.substring(lastIdx).replace(/^\s*of\s+/i, '').trim();
      } else {
        // No "of" — treat as industry (user can split further if needed)
        result.industry = working;
      }
    }

    return result;
  };

  const loadHistorySearch = (item: SearchHistoryItem) => {
    setGeneratedLeads(item.leads);
    setNoLeadsState({ show: false, searchPrompt: '', suggestions: [] });
    // Restore the prompt and filters so "Generate more" has context.
    setSearchPrompt(item.searchPrompt);
    const parsed = parsePromptIntoFilters(item.searchPrompt);
    if (parsed.title) setFilterTitle(parsed.title);
    if (parsed.industry) setFilterIndustry(parsed.industry);
    if (parsed.location) setFilterLocation(parsed.location);
    toast.info(`Loaded ${item.leadCount} ${item.leadCount === 1 ? 'lead' : 'leads'} from previous search`);
  };
  const [attrDialogOpen, setAttrDialogOpen] = useState(false);
  const [pendingAddLead, setPendingAddLead] = useState<{ lead: GeneratedLead; contactData?: { contactName?: string; email?: string; phone?: string } } | null>(null);
  const [attrDialogLoading, setAttrDialogLoading] = useState(false);
  const [fromDatabase, setFromDatabase] = useState(0);
  const [fromAI, setFromAI] = useState(0);

  // Countdown timer state
  const [countdownSeconds, setCountdownSeconds] = useState(0);

  // Track current AI job to prevent duplicate requests
  const [currentAiJobId, setCurrentAiJobId] = useState<string | null>(null);

  // localStorage key for persisting active pipeline job across navigation
  const PIPELINE_JOB_KEY = 'wyni_active_pipeline_job';

  // No leads found state with suggestions
  type AISuggestion = { title: string; industry: string; location: string };
  const [noLeadsState, setNoLeadsState] = useState<{
    show: boolean;
    searchPrompt: string;
    suggestions: string[];
    reason?: 'timeout' | 'empty';
    aiSuggestedQueries?: AISuggestion[];
  }>({ show: false, searchPrompt: '', suggestions: [] });

  // Slow-search banner shown during an in-progress search that's taking too long
  // (pipeline reports intermediate fallback suggestions after the first attempt times out).
  const [slowSearchBanner, setSlowSearchBanner] = useState<{
    show: boolean;
    dismissed: boolean;
    suggestions: AISuggestion[];
  }>({ show: false, dismissed: false, suggestions: [] });

  // Hide the slow-search banner automatically when a search ends (success or fail).
  useEffect(() => {
    if (!isSearching && slowSearchBanner.show) {
      setSlowSearchBanner({ show: false, dismissed: false, suggestions: [] });
    }
  }, [isSearching]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================
  // Browser notifications — let users walk away during long searches
  // ============================================================
  const notificationsSupported = typeof window !== 'undefined' && 'Notification' in window;
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission | 'unsupported'>(
    notificationsSupported ? Notification.permission : 'unsupported'
  );

  // Ask the user once when they kick off a search. Browsers ignore repeated
  // requests after the user has already chosen, so this is safe to call
  // every time without spamming them.
  const requestNotificationPermission = async () => {
    if (!notificationsSupported || Notification.permission !== 'default') return;
    try {
      const result = await Notification.requestPermission();
      setNotificationPermission(result);
    } catch {
      // Some older browsers throw — ignore, we'll fall back to in-page UI
    }
  };

  // Fire a browser notification, but only if the tab is hidden. If the tab
  // is visible the user is already looking at the result UI, so spamming
  // them with an OS-level popup is annoying.
  const notifyPipelineDone = (
    kind: 'success' | 'empty' | 'failed',
    payload: { count?: number; searchPrompt: string; message?: string }
  ) => {
    if (!notificationsSupported || Notification.permission !== 'granted') return;
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') return;

    const titles = {
      success: 'Your Leadgenix search is ready',
      empty: 'Search finished — no leads found',
      failed: 'Search didn\'t complete',
    };
    const bodies = {
      success: `Found ${payload.count ?? 0} leads for "${payload.searchPrompt}"`,
      empty: `We couldn't find leads for "${payload.searchPrompt}". Try a different search.`,
      failed: payload.message || `Something went wrong with "${payload.searchPrompt}".`,
    };

    try {
      const n = new Notification(titles[kind], {
        body: bodies[kind],
        icon: '/logo-512.png',
        badge: '/favicon-32.png',
        tag: 'leadgenix-pipeline', // collapse multiple notifications into one
      });
      n.onclick = () => {
        window.focus();
        n.close();
      };
    } catch {
      // Notification API can throw on some browsers/configs — silently ignore
    }
  };

  // Clarification questions state (for AI multi-turn conversations)
  const [clarificationState, setClarificationState] = useState<ClarificationState>({
    isAskingQuestions: false,
    questions: [],
    answers: [],
    chatHistory: [],
  });

  // Schedule Demo dialog state
  const [scheduleDemoOpen, setScheduleDemoOpen] = useState(false);
  const [scheduleDemoLead, setScheduleDemoLead] = useState<LeadSearchResult | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState(demoEmailTemplates[0].id);
  const [demoDate, setDemoDate] = useState<Date | null>(new Date(Date.now() + 24 * 60 * 60 * 1000)); // Tomorrow
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [isSendingDemo, setIsSendingDemo] = useState(false);

  // Progress stage texts for AI discovery
  const progressStages = [
    { progress: 0, text: 'Initializing AI search...' },
    { progress: 10, text: 'Analyzing search prompt...' },
    { progress: 20, text: 'Selecting data sources...' },
    { progress: 35, text: 'Searching company databases...' },
    { progress: 50, text: 'Discovering potential leads...' },
    { progress: 65, text: 'Analyzing company profiles...' },
    { progress: 80, text: 'Scoring and ranking leads...' },
    { progress: 90, text: 'Finalizing results...' },
    { progress: 100, text: 'Complete!' },
  ];

  // Get progress text based on progress value
  const getProgressText = (progress: number): string => {
    for (let i = progressStages.length - 1; i >= 0; i--) {
      if (progress >= progressStages[i].progress) {
        return progressStages[i].text;
      }
    }
    return progressStages[0].text;
  };

  // Track search start time for estimated remaining calculation
  const searchStartTimeRef = useRef<number | null>(null);

  // Calculate estimated remaining time based on progress
  useEffect(() => {
    if (isSearching || isPollingAI) {
      if (!searchStartTimeRef.current) {
        searchStartTimeRef.current = Date.now();
      }

      if (aiProgress >= 100) {
        setCountdownSeconds(0);
      } else if (aiProgress >= 20) {
        // Once polling starts (progress >= 20%), use elapsed time to estimate
        const elapsedSeconds = (Date.now() - searchStartTimeRef.current) / 1000;
        // Typical total time is 60-120s; use progress ratio but with a realistic floor
        const estimatedTotalTime = Math.max(60, elapsedSeconds / (aiProgress / 100));
        const remainingTime = Math.ceil(estimatedTotalTime - elapsedSeconds);
        setCountdownSeconds(Math.max(10, Math.min(180, remainingTime)));
      } else {
        // Early stage (0-20%): show a realistic initial estimate
        setCountdownSeconds(90);
      }
    } else {
      searchStartTimeRef.current = null;
      setCountdownSeconds(0);
    }
  }, [isSearching, isPollingAI, aiProgress]);

  const showRawResults = (results: LeadSearchResult[]) => {
    setSearchResults(results);
    setSelectedResults([]);
    setPage(1);
  };

  // Translate raw pipeline errors into user-friendly messages
  const formatPipelineError = (raw?: string | null): string => {
    const msg = (raw || '').toLowerCase();
    if (msg.includes('timed out') || msg.includes('timeout')) {
      return 'Our AI is taking longer than usual. Please try a more specific search or try again in a moment.';
    }
    if (msg.includes('unauthorized') || msg.includes('401')) {
      return 'AI service authentication issue. Our team has been notified.';
    }
    if (msg.includes('rate limit') || msg.includes('429')) {
      return 'Too many searches right now. Please wait a minute and try again.';
    }
    if (msg.includes('no companies') || msg.includes('0 companies')) {
      return "We couldn't find matching leads. Try being more specific or exploring a related industry.";
    }
    return 'Search didn\'t complete. Please try again with a different query.';
  };

  // Generate helpful suggestions when no leads are found
  const generateNoLeadsSuggestions = (prompt: string): string[] => {
    const suggestions: string[] = [];
    const lowerPrompt = prompt.toLowerCase();

    // Check if prompt is too short/general
    if (prompt.trim().split(' ').length <= 2) {
      suggestions.push('Try adding more details like industry, location, or company size');
      suggestions.push('Example: "Technology companies in Hong Kong with 50+ employees"');
    }

    // Check if missing location
    const locations = ['hong kong', 'singapore', 'malaysia', 'thailand', 'china', 'japan', 'taiwan', 'asia'];
    const hasLocation = locations.some(loc => lowerPrompt.includes(loc));
    if (!hasLocation) {
      suggestions.push('Try specifying a location (e.g., Hong Kong, Singapore, Asia Pacific)');
    }

    // Check if missing industry
    const industries = ['technology', 'tech', 'software', 'retail', 'hospitality', 'restaurant', 'finance', 'healthcare', 'manufacturing', 'logistics'];
    const hasIndustry = industries.some(ind => lowerPrompt.includes(ind));
    if (!hasIndustry) {
      suggestions.push('Try specifying an industry (e.g., Technology, Retail, Hospitality)');
    }

    // Check if too specific/complex
    if (prompt.trim().split(' ').length > 10) {
      suggestions.push('Your search might be too specific. Try simplifying it');
      suggestions.push('Focus on 2-3 key criteria: industry + location + size');
    }

    // Always add some general tips
    if (suggestions.length === 0) {
      suggestions.push('Try broadening your search criteria');
      suggestions.push('Use different keywords or industry terms');
    }

    suggestions.push('Try searching for related industries or adjacent markets');

    return suggestions.slice(0, 4); // Max 4 suggestions
  };

  // Handle clicking a suggested prompt chip
  const handleSuggestedPromptClick = (prompt: string) => {
    setSearchPrompt(prompt);
    // Clear clarification state when user selects a new prompt
    setClarificationState({
      isAskingQuestions: false,
      questions: [],
      answers: [],
      chatHistory: [],
    });
  };

  // Handle answering clarification questions
  const handleAnswerQuestions = async (answers: string[]) => {
    // Build chat history with previous Q&A
    const newHistory: ChatMessage[] = [
      ...clarificationState.chatHistory,
      {
        role: 'assistant',
        content: 'QUESTIONS:\n' + clarificationState.questions.map((q, i) => `${i + 1}. ${q}`).join('\n'),
      },
      {
        role: 'user',
        content: answers.join('\n'),
      },
    ];

    // Reset clarification state and update chat history
    setClarificationState({
      isAskingQuestions: false,
      questions: [],
      answers: [],
      chatHistory: newHistory,
    });

    // Re-submit search with chat history
    toast.info('Searching with your answers...');

    // Trigger a new search with the chat history
    await handleDatabaseSearchWithHistory(newHistory);
  };

  // Handle canceling clarification questions
  const handleCancelClarification = () => {
    setClarificationState({
      isAskingQuestions: false,
      questions: [],
      answers: [],
      chatHistory: [],
    });
  };

  // AI-only search with chat history (for clarification flow)
  const handleDatabaseSearchWithHistory = async (chatHistory: ChatMessage[]) => {
    if (!searchPrompt.trim()) {
      toast.warning('Please enter a search prompt');
      return;
    }

    // Check if an AI job is already running
    if (currentAiJobId && isPollingAI) {
      toast.info('AI search is already in progress. Please wait for it to complete.');
      return;
    }

    setIsSearching(true);
    setPage(1);
    setGeneratedLeads([]);
    setSearchResults([]);
    setCurrentAiJobId(null); // Reset job ID for new search
    setNoLeadsState({ show: false, searchPrompt: '', suggestions: [] }); // Reset no leads state
    // Initialize progress
    setAiProgress(5);
    setAiProgressText('Initializing AI search...');

    try {
      // Start AI discovery directly - skip database search
      setAiProgress(10);
      setAiProgressText('Starting AI lead discovery...');

      const result = await leadGenerationApi.searchGeneratedLeads({
        prompt: searchPrompt.trim(),
        filters: {
          industry: extractIndustries(searchPrompt)[0],
          location: extractLocation(searchPrompt),
        },
        page: 1,
        limit: 30,
        includeAIDiscovery: true,
        skipDatabaseSearch: true, // Skip database, go directly to AI
        chatHistory,
      });

      // AI discovery should always be started when skipDatabaseSearch is true
      if (result.aiDiscoveryStarted && result.aiJobId) {
        setCurrentAiJobId(result.aiJobId);
        pollForAIResultsWithHistory(result.aiJobId, chatHistory);
      } else {
        // Fallback: If AI didn't start for some reason, show suggestions
        setAiProgress(100);
        setAiProgressText('Complete!');
        setNoLeadsState({
          show: true,
          searchPrompt: searchPrompt,
          suggestions: generateNoLeadsSuggestions(searchPrompt)
        });
        setTimeout(() => setAiProgress(0), 500);
        setIsSearching(false);
      }
    } catch (error: any) {
      console.error('Error starting AI search:', error);
      toast.error(error.message || 'Failed to start AI search');
      setAiProgress(0);
      setIsSearching(false);
    }
  };

  // Poll for AI results with history - streams results incrementally
  const pollForAIResultsWithHistory = async (jobId: string, chatHistory: ChatMessage[]) => {
    let pollCount = 0;
    const maxPolls = 30; // Poll for max 300 seconds (30 polls * 10 seconds)
    const pollInterval = 10000; // 10 seconds for faster incremental updates
    let seenResultIds = new Set<string>();

    setIsPollingAI(true); // Start polling indicator
    setAiProgress(20);
    setAiProgressText('Selecting data sources...');

    const poll = async () => {
      try {
        // Check job status for clarification needs and real progress
        const jobStatus = await aiCrmApi.leadGeneration.getJobStatus(jobId);

        if (jobStatus.status === 'needs_clarification' && jobStatus.questions) {
          setClarificationState({
            isAskingQuestions: true,
            questions: jobStatus.questions,
            answers: [],
            chatHistory: chatHistory,
          });
          setIsPollingAI(false);
          setIsSearching(false);
          setAiProgress(0);
          setCurrentAiJobId(null);
          return;
        }

        // Use real progress from backend if available, otherwise simulate
        if (jobStatus.progress && jobStatus.progress > 0) {
          setAiProgress(Math.min(jobStatus.progress, 95));
          setAiProgressText(getProgressText(jobStatus.progress));
        } else {
          const simulatedProgress = Math.min(20 + (pollCount * 2.5), 90);
          setAiProgress(simulatedProgress);
          setAiProgressText(getProgressText(simulatedProgress));
        }

        const jobResults = await aiCrmApi.leadGeneration.getJobResults(jobId, aiResultsQuery);

        // Show partial results incrementally as they arrive
        if (jobResults.results && jobResults.results.length > 0) {
          const newResults = jobResults.results.filter((r: any) => !seenResultIds.has(r.id));
          if (newResults.length > 0) {
            newResults.forEach((r: any) => seenResultIds.add(r.id));
            showRawResults(jobResults.results);
            setAiProgressText(`Discovered ${jobResults.results.length} leads so far...`);
            setNoLeadsState({ show: false, searchPrompt: '', suggestions: [] });

            // Store incrementally so cards appear in the UI
            await leadGenerationApi.storeAIResults(jobResults.results, searchPrompt);
            const updatedResults = await leadGenerationApi.searchGeneratedLeads({
              prompt: searchPrompt.trim(),
              page: 1,
              limit: 30,
              includeAIDiscovery: false,
            });
            // Don't show leads yet — wait for enrichment to complete
            setFromDatabase(updatedResults.fromDatabase);
            setFromAI(updatedResults.fromAI);
          }
        }

        // Check if job is complete
        const isJobDone = jobStatus.status === 'completed' || jobStatus.status === 'failed';

        if (isJobDone) {
          if (seenResultIds.size > 0) {
            setAiProgress(100);
            setAiProgressText('Enriching contacts...');
            queryClient.invalidateQueries({ queryKey: ['campaignHints'] });
            // Fetch leads and trigger backend batch enrichment
            const currentLeads = await leadGenerationApi.searchGeneratedLeads({
              prompt: searchPrompt.trim(), page: 1, limit: 30, includeAIDiscovery: false,
            });
            toast.success(`AI discovered ${seenResultIds.size} leads! Finding contacts...`);
            triggerBatchEnrichAndPoll(currentLeads.results);
          } else {
            setAiProgress(100);
            setAiProgressText('Complete!');
            setNoLeadsState({
              show: true,
              searchPrompt: searchPrompt,
              suggestions: generateNoLeadsSuggestions(searchPrompt)
            });
          }
          setIsPollingAI(false);
          setIsSearching(false);
          setTimeout(() => setAiProgress(0), 500);
          setCurrentAiJobId(null);
          return;
        }

        pollCount++;
        if (pollCount < maxPolls) {
          setTimeout(poll, pollInterval);
        } else {
          // Max polls reached
          if (seenResultIds.size > 0) {
            setAiProgress(100);
            setAiProgressText('Enriching contacts...');
            toast.success(`AI discovered ${seenResultIds.size} leads! Now finding contacts...`);
            queryClient.invalidateQueries({ queryKey: ['campaignHints'] });
            // Auto-enrich leads in background
            const currentLeads = await leadGenerationApi.searchGeneratedLeads({
              prompt: searchPrompt.trim(), page: 1, limit: 30, includeAIDiscovery: false,
            });
            triggerBatchEnrichAndPoll(currentLeads.results);
          } else {
            setNoLeadsState({
              show: true,
              searchPrompt: searchPrompt,
              suggestions: generateNoLeadsSuggestions(searchPrompt)
            });
          }
          setIsPollingAI(false);
          setIsSearching(false);
          setTimeout(() => setAiProgress(0), 500);
          setCurrentAiJobId(null);
        }
      } catch (error) {
        console.error('Error polling AI results:', error);
        pollCount++;
        if (pollCount < maxPolls) {
          const backoffDelay = Math.min(pollInterval * Math.pow(1.5, Math.floor(pollCount / 3)), 30000);
          setTimeout(poll, backoffDelay);
        } else {
          setIsPollingAI(false);
          setIsSearching(false);
          setAiProgress(0);
          setCurrentAiJobId(null);
          if (seenResultIds.size === 0) {
            setNoLeadsState({
              show: true,
              searchPrompt: searchPrompt,
              suggestions: generateNoLeadsSuggestions(searchPrompt)
            });
          }
        }
      }
    };

    setTimeout(poll, 5000); // Initial delay of 5 seconds before first poll
  };

  // Helper to extract industries from prompt
  const extractIndustries = (prompt: string): string[] => {
    const industryKeywords: Record<string, string> = {
      'technology': 'Technology',
      'tech': 'Technology',
      'software': 'Technology',
      'retail': 'Retail',
      'manufacturing': 'Manufacturing',
      'healthcare': 'Healthcare',
      'medical': 'Healthcare',
      'finance': 'Finance',
      'financial': 'Finance',
      'fintech': 'Finance',
      'education': 'Education',
      'edtech': 'Education',
      'real estate': 'Real Estate',
      'property': 'Real Estate',
      'hospitality': 'Hospitality',
      'hotel': 'Hospitality',
      'saas': 'Technology',
      'b2b': 'Professional Services',
      'startup': 'Technology',
      'sme': 'Professional Services'
    };

    const lowerPrompt = prompt.toLowerCase();
    const foundIndustries = new Set<string>();

    for (const [keyword, industry] of Object.entries(industryKeywords)) {
      if (lowerPrompt.includes(keyword)) {
        foundIndustries.add(industry);
      }
    }

    return Array.from(foundIndustries);
  };

  // Helper to extract location from prompt
  const extractLocation = (prompt: string): string => {
    const locations = [
      'Hong Kong', 'Singapore', 'Malaysia', 'Thailand', 'Indonesia',
      'Philippines', 'Vietnam', 'Taiwan', 'China', 'Japan',
      'South Korea', 'Australia', 'Asia Pacific', 'Asia'
    ];

    const lowerPrompt = prompt.toLowerCase();

    for (const location of locations) {
      if (lowerPrompt.includes(location.toLowerCase())) {
        return location;
      }
    }

    return 'Hong Kong'; // Default location
  };

  // AI-only search - skip database, always use AI discovery
  const handleGenerateMore = async () => {
    // Generate 10 more leads, excluding companies already found
    const existingNames = generatedLeads.map(l => l.companyName).join(', ');
    const excludeContext = `Exclude these companies that were already found: ${existingNames}`;
    const originalContext = filterTrigger.trim();
    const combinedContext = originalContext ? `${originalContext}. ${excludeContext}` : excludeContext;

    // Pull industry/location from whichever source is available. Filters are
    // populated either by typing into the form OR by loading a history item
    // (which calls parsePromptIntoFilters). If still empty, parse the current
    // searchPrompt as a last resort.
    let industryValue = filterIndustry.trim();
    let locationValue = filterLocation.trim();
    let titleValue = filterTitle.trim();
    if (!industryValue || !locationValue) {
      const parsed = parsePromptIntoFilters(searchPrompt);
      industryValue = industryValue || parsed.industry;
      locationValue = locationValue || parsed.location || extractLocation(searchPrompt);
      titleValue = titleValue || parsed.title;
    }
    if (!industryValue || !locationValue) {
      toast.warning('Please do a search first so we know what to look for.');
      return;
    }

    // Pipelines take 5-10 min — ask once for permission to notify when done
    requestNotificationPermission();

    setIsSearching(true);
    setIsPollingAI(true);
    setAiProgress(15);
    setAiProgressText('Finding more companies and contacts...');

    try {
      const result = await leadGenerationApi.discoverAndEnrich({
        industry: industryValue,
        location: locationValue,
        targetRole: titleValue || 'CEO',
        optionalContext: combinedContext,
        count: 10,
      });
      window.dispatchEvent(new Event('pipeline-queue-enqueued'));

      const currentSearchPrompt = result.searchPrompt || searchPrompt;
      const searchStartedAt = new Date().toISOString();
      const previousLeads = [...generatedLeads]; // Keep existing leads
      toast.info('AI is finding more companies and contacts...');

      const pollInterval = 5000;
      const maxSilentPolls = 60; // Only timeout after 60 polls with no pipeline activity
      let silentPollCount = 0;
      let lastCount = 0;
      let pipelineDone = false;
      let lastStatus = '';

      // Track this Find-More-Leads job for the Stop Search button
      currentJobIdRef.current = result.jobId;

      const poll = async () => {
        if (cancelledJobsRef.current.has(result.jobId)) {
          return;
        }
        try {
          let pipelineActive = false;
          try {
            const statusResp = await apiClient.get(`/lead-generation/pipeline-status/${result.jobId}`);
            const jobStatus = statusResp.data?.data;
            if (jobStatus?.status === 'CANCELLED') {
              cancelledJobsRef.current.add(result.jobId);
              setAiProgress(0); setIsSearching(false); setIsPollingAI(false);
              if (currentJobIdRef.current === result.jobId) currentJobIdRef.current = null;
              return;
            }
            if (jobStatus) {
              if (jobStatus.statusMessage) setAiProgressText(jobStatus.statusMessage);
              if (jobStatus.status !== lastStatus) { lastStatus = jobStatus.status; }
              if (
                Array.isArray(jobStatus.fallbackSuggestions) &&
                jobStatus.fallbackSuggestions.length > 0 &&
                (jobStatus.status === 'SEARCHING' || jobStatus.status === 'ENRICHING')
              ) {
                setSlowSearchBanner(prev =>
                  prev.dismissed
                    ? prev
                    : { show: true, dismissed: false, suggestions: jobStatus.fallbackSuggestions }
                );
              }
              if (jobStatus.status === 'WAITING') { setAiProgress(10); pipelineActive = true; }
              else if (jobStatus.status === 'SEARCHING') { setAiProgress(20); pipelineActive = true; }
              else if (jobStatus.status === 'ENRICHING') { setAiProgress(50); pipelineActive = true; }
              else if (jobStatus.status === 'VERIFYING') { setAiProgress(70); pipelineActive = true; }
              else if (jobStatus.status === 'STORING') { setAiProgress(85); pipelineActive = true; }
              else if (jobStatus.status === 'DONE') pipelineDone = true;
              else if (jobStatus.status === 'FAILED') {
                const friendlyMsg = formatPipelineError(jobStatus.error);
                const isTimeout = /timed? out|timeout/i.test(jobStatus.error || '');
                toast.error(friendlyMsg);
                setNoLeadsState({
                  show: true,
                  searchPrompt,
                  suggestions: generateNoLeadsSuggestions(searchPrompt),
                  reason: isTimeout ? 'timeout' : 'empty',
                  aiSuggestedQueries: jobStatus.fallbackSuggestions || [],
                });
                notifyPipelineDone('failed', { searchPrompt: currentSearchPrompt, message: friendlyMsg });
                setAiProgress(0); setIsSearching(false); setIsPollingAI(false);
                return;
              }
            }
          } catch {}

          const searchResult = await leadGenerationApi.searchGeneratedLeads({
            prompt: currentSearchPrompt, page: 1, limit: 30,
            includeAIDiscovery: false, createdAfter: searchStartedAt,
          });
          const newLeads = (searchResult.results || []).filter((l: any) => l.enrichmentStatus === 'COMPLETED');

          if (newLeads.length > lastCount) {
            // Append new leads to existing ones
            setGeneratedLeads([...previousLeads, ...newLeads]);
            lastCount = newLeads.length;
          }

          if (pipelineActive) {
            silentPollCount = 0;
          } else {
            silentPollCount++;
          }

          const isDone = pipelineDone || newLeads.length >= 10;
          const isTimedOut = !pipelineActive && silentPollCount >= maxSilentPolls;

          if (isDone || isTimedOut) {
            if (newLeads.length > 0) {
              setAiProgress(100);
              setAiProgressText('Complete!');
              setGeneratedLeads([...previousLeads, ...newLeads]);
              toast.success(`Found ${newLeads.length} more leads!`);
              setSearchHistory(prev => {
                const newEntry = {
                  searchPrompt: currentSearchPrompt,
                  leadCount: previousLeads.length + newLeads.length,
                  latestDate: new Date().toISOString(),
                  leads: [...previousLeads, ...newLeads],
                };
                return [newEntry, ...prev];
              });
              notifyPipelineDone('success', { count: newLeads.length, searchPrompt: currentSearchPrompt });
            } else {
              toast.info('No additional leads found.');
              notifyPipelineDone('empty', { searchPrompt: currentSearchPrompt });
            }
            setTimeout(() => { setAiProgress(0); setIsSearching(false); setIsPollingAI(false); }, 500);
            return;
          }
          setTimeout(poll, pollInterval);
        } catch (err) {
          silentPollCount++;
          if (silentPollCount < maxSilentPolls) setTimeout(poll, pollInterval);
          else { setAiProgress(0); setIsSearching(false); setIsPollingAI(false); }
        }
      };
      setTimeout(poll, 8000);
    } catch (error: any) {
      if (error?.response?.status === 429 && error?.response?.data?.code === 'PER_USER_CAP_EXCEEDED') {
        toast.warning('You have reached the per-user limit of 10 queued searches. Please wait for some to finish or stop a few from the queue tray.');
        setAiProgress(0); setIsSearching(false); setIsPollingAI(false);
        return;
      }
      toast.error(error.message || 'Failed to generate more leads.');
      setAiProgress(0); setIsSearching(false); setIsPollingAI(false);
    }
  };

  // Best-effort UI cancel — stop polling immediately, mark backend as CANCELLED
  // so the Python pipeline's eventual results are discarded. Partial leads already
  // saved into the DB stay visible.
  const handleStopSearch = async () => {
    const jobId = currentJobIdRef.current;
    if (jobId) {
      cancelledJobsRef.current.add(jobId);
      currentJobIdRef.current = null;
      try {
        await apiClient.post(`/lead-generation/pipeline-cancel/${jobId}`);
      } catch {
        // Best-effort — even if cancel fails, the frontend has already stopped polling
      }
    }
    localStorage.removeItem(PIPELINE_JOB_KEY);
    setAiProgress(0);
    setIsSearching(false);
    setIsPollingAI(false);
    setSlowSearchBanner({ show: false, dismissed: false, suggestions: [] });
    toast.info('Search stopped. Partial results are kept.');
  };

  const handleDatabaseSearch = async () => {
    if (!filterIndustry.trim() || !filterLocation.trim()) {
      toast.warning('Please fill in Industry and Location');
      return;
    }

    if (isSearching) {
      toast.info('Search is already in progress. Please wait.');
      return;
    }

    // Pipelines take 5-10 min — ask once for permission to notify when done
    requestNotificationPermission();

    setIsSearching(true);
    setIsPollingAI(true);
    setPage(1);
    setGeneratedLeads([]);
    setSearchResults([]);
    setNoLeadsState({ show: false, searchPrompt: '', suggestions: [] });
    setAiProgress(15);
    setAiProgressText('Finding companies and contacts...');

    try {
      // Fire-and-forget: start pipeline (returns immediately)
      const result = await leadGenerationApi.discoverAndEnrich({
        industry: filterIndustry.trim(),
        location: filterLocation.trim(),
        targetRole: filterTitle.trim() || 'CEO',
        optionalContext: filterTrigger.trim(),
        count: 10,
      });
      window.dispatchEvent(new Event('pipeline-queue-enqueued'));

      const currentSearchPrompt = result.searchPrompt || searchPrompt;
      const searchStartedAt = new Date().toISOString();

      // Persist job so user can navigate away and come back
      localStorage.setItem(PIPELINE_JOB_KEY, JSON.stringify({
        jobId: result.jobId,
        searchPrompt: currentSearchPrompt,
        searchStartedAt,
        startedAt: Date.now(),
      }));

      toast.info('AI is finding companies and contacts... You can navigate away and come back.');
      startPipelinePolling(result.jobId, currentSearchPrompt, searchStartedAt);
    } catch (error: any) {
      console.error('Error in discover-and-enrich:', error);
      if (error?.response?.status === 429 && error?.response?.data?.code === 'PER_USER_CAP_EXCEEDED') {
        toast.warning('You have reached the per-user limit of 10 queued searches. Please wait for some to finish or stop a few from the queue tray.');
        setAiProgress(0);
        setIsSearching(false);
        setIsPollingAI(false);
        return;
      }
      toast.error(error.message || 'Search failed. Please try again.');
      setNoLeadsState({ show: true, searchPrompt, suggestions: generateNoLeadsSuggestions(searchPrompt) });
      setAiProgress(0);
      setIsSearching(false);
      setIsPollingAI(false);
    }
  };

  // Poll for AI discovery results and store them - streams results incrementally
  const pollForAIResults = async (jobId: string) => {
    let pollCount = 0;
    const maxPolls = 30; // Poll for max 300 seconds (30 polls * 10 seconds)
    const pollInterval = 10000; // 10 seconds for faster incremental updates
    let seenResultIds = new Set<string>();

    setIsPollingAI(true); // Start polling indicator
    setAiProgress(5); // Initial progress
    setAiProgressText('Initializing AI search...');

    const poll = async () => {
      try {
        // Check job status for real progress and clarification
        const jobStatus = await aiCrmApi.leadGeneration.getJobStatus(jobId);

        if (jobStatus.status === 'needs_clarification' && jobStatus.questions?.length) {
          setClarificationState({
            isAskingQuestions: true,
            questions: jobStatus.questions,
            answers: [],
            chatHistory: [],
          });
          setIsPollingAI(false);
          setIsSearching(false);
          setAiProgress(0);
          return;
        }

        // Use real progress from backend if available, otherwise simulate
        if (jobStatus.progress && jobStatus.progress > 0) {
          setAiProgress(Math.min(jobStatus.progress, 95));
          setAiProgressText(getProgressText(jobStatus.progress));
        } else {
          const simulatedProgress = Math.min(5 + (pollCount * 3), 90);
          setAiProgress(simulatedProgress);
          setAiProgressText(getProgressText(simulatedProgress));
        }

        const jobResults = await aiCrmApi.leadGeneration.getJobResults(jobId, aiResultsQuery);

        // Show partial results incrementally as they arrive
        if (jobResults.results && jobResults.results.length > 0) {
          const newResults = jobResults.results.filter((r: any) => !seenResultIds.has(r.id));
          if (newResults.length > 0) {
            newResults.forEach((r: any) => seenResultIds.add(r.id));
            showRawResults(jobResults.results);
            setAiProgressText(`Discovered ${jobResults.results.length} leads so far...`);
            setNoLeadsState({ show: false, searchPrompt: '', suggestions: [] });

            // Store incrementally so cards appear in the UI
            await leadGenerationApi.storeAIResults(jobResults.results, searchPrompt);
            const updatedResults = await leadGenerationApi.searchGeneratedLeads({
              prompt: searchPrompt.trim(),
              page: 1,
              limit: 30,
              includeAIDiscovery: false,
            });
            // Don't show leads yet — wait for enrichment to complete
            setFromDatabase(updatedResults.fromDatabase);
            setFromAI(updatedResults.fromAI);
          }
        }

        // Check if job is complete
        const isJobDone = jobStatus.status === 'completed' || jobStatus.status === 'failed';

        if (isJobDone) {
          if (seenResultIds.size > 0) {
            setAiProgress(100);
            setAiProgressText('Enriching contacts...');
            queryClient.invalidateQueries({ queryKey: ['campaignHints'] });
            // Fetch leads and trigger backend batch enrichment
            const currentLeads = await leadGenerationApi.searchGeneratedLeads({
              prompt: searchPrompt.trim(), page: 1, limit: 30, includeAIDiscovery: false,
            });
            toast.success(`AI discovered ${seenResultIds.size} leads! Finding contacts...`);
            triggerBatchEnrichAndPoll(currentLeads.results);
          } else {
            setAiProgress(100);
            setAiProgressText('Complete!');
            setNoLeadsState({
              show: true,
              searchPrompt: searchPrompt,
              suggestions: generateNoLeadsSuggestions(searchPrompt)
            });
          }
          setIsPollingAI(false);
          setIsSearching(false);
          setTimeout(() => setAiProgress(0), 500);
          setCurrentAiJobId(null);
          return;
        }

        pollCount++;
        if (pollCount < maxPolls) {
          setTimeout(poll, pollInterval);
        } else {
          // Max polls reached
          if (seenResultIds.size > 0) {
            setAiProgress(100);
            setAiProgressText('Enriching contacts...');
            toast.success(`AI discovered ${seenResultIds.size} leads! Now finding contacts...`);
            queryClient.invalidateQueries({ queryKey: ['campaignHints'] });
            // Auto-enrich leads in background
            const currentLeads = await leadGenerationApi.searchGeneratedLeads({
              prompt: searchPrompt.trim(), page: 1, limit: 30, includeAIDiscovery: false,
            });
            triggerBatchEnrichAndPoll(currentLeads.results);
          } else {
            setNoLeadsState({
              show: true,
              searchPrompt: searchPrompt,
              suggestions: generateNoLeadsSuggestions(searchPrompt)
            });
          }
          setIsPollingAI(false);
          setIsSearching(false);
          setTimeout(() => setAiProgress(0), 500);
          setCurrentAiJobId(null);
        }
      } catch (error) {
        console.error('Error polling AI results:', error);
        pollCount++;
        if (pollCount < maxPolls) {
          const backoffDelay = Math.min(pollInterval * Math.pow(1.5, Math.floor(pollCount / 3)), 30000);
          setTimeout(poll, backoffDelay);
        } else {
          setIsPollingAI(false);
          setIsSearching(false);
          setAiProgress(0);
          setCurrentAiJobId(null);
          if (seenResultIds.size === 0) {
            setNoLeadsState({
              show: true,
              searchPrompt: searchPrompt,
              suggestions: generateNoLeadsSuggestions(searchPrompt)
            });
          }
        }
      }
    };

    setTimeout(poll, 5000); // Initial delay of 5 seconds before first poll
  };

  // Handle viewing lead details in modal
  const handleViewDetails = (lead: GeneratedLead) => {
    setSelectedGeneratedLead(lead);
    setDetailModalOpen(true);
  };

  // Handle adding generated lead to CRM — opens attribute selection dialog first
  const handleAddGeneratedLeadToCRM = async (
    lead: GeneratedLead,
    contactData?: { contactName?: string; email?: string; phone?: string }
  ) => {
    setPendingAddLead({ lead, contactData });
    setAttrDialogOpen(true);
  };

  // Actually add to CRM after attribute selection (or skip)
  const handleConfirmAddToCRM = async (attributes: Record<string, string>) => {
    if (!pendingAddLead) return;
    setAttrDialogLoading(true);
    try {
      const { lead, contactData } = pendingAddLead;
      const payload = {
        ...contactData,
        customAttributes: Object.keys(attributes).length > 0 ? attributes : undefined,
      };
      await leadGenerationApi.addGeneratedLeadToCRM(lead.id, payload);
      toast.success(`Contact added to CRM from "${lead.companyName}" successfully!`);
      setAttrDialogOpen(false);
      setPendingAddLead(null);
    } catch (error: any) {
      console.error('Error adding to CRM:', error);
      toast.error(error.response?.data?.message || 'Failed to add to CRM');
    } finally {
      setAttrDialogLoading(false);
    }
  };

  // Retry enrichment for a failed lead
  const handleRetryEnrich = async (lead: GeneratedLead) => {
    try {
      // Reset status to PENDING so the card shows loading
      setGeneratedLeads(prev => prev.map(l =>
        l.id === lead.id ? { ...l, enrichmentStatus: 'PENDING' } : l
      ));
      await leadGenerationApi.enrichGeneratedLead(lead.id);
      // Refresh list to get updated data
      const updatedResults = await leadGenerationApi.searchGeneratedLeads({
        prompt: searchPrompt.trim(),
        page: 1,
        limit: 30,
        includeAIDiscovery: false,
      });
      setGeneratedLeads(updatedResults.results);
    } catch (err: any) {
      console.warn(`Retry enrich failed for ${lead.companyName}:`, err.message);
      // Refresh to show FAILED state again
      const updatedResults = await leadGenerationApi.searchGeneratedLeads({
        prompt: searchPrompt.trim(),
        page: 1,
        limit: 30,
        includeAIDiscovery: false,
      });
      setGeneratedLeads(updatedResults.results);
      toast.error(`Could not find contacts for ${lead.companyName}`);
    }
  };

  // Handle rejecting a generated lead
  const handleRejectGeneratedLead = async (leadId: string) => {
    try {
      await leadGenerationApi.rejectGeneratedLead(leadId);
      toast.info('Lead marked as not interested');
      // Remove from list
      setGeneratedLeads((prev) => prev.filter((l) => l.id !== leadId));
    } catch (error: any) {
      console.error('Error rejecting lead:', error);
      throw new Error(error.response?.data?.message || 'Failed to reject lead');
    }
  };

  // Handle quick add (when contact email is available) — also opens attribute dialog
  // Enrichment progress state
  const [autoEnrichProgress, setAutoEnrichProgress] = useState<{ total: number; done: number; estimatedMinLeft: number; overEstimate: boolean } | null>(null);
  const enrichPollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enrichStartTimeRef = useRef<number>(0);

  // Start backend batch enrichment + progressive polling
  const triggerBatchEnrichAndPoll = async (leads: GeneratedLead[]) => {
    const allLeadIds = leads.map(l => l.id);

    // Show all leads immediately (cards with loading shimmer for pending ones)
    setGeneratedLeads(leads);

    // Trigger backend batch enrichment (fire-and-forget on backend)
    const pendingIds = leads.filter(l => l.enrichmentStatus === 'PENDING' || l.enrichmentStatus === 'FAILED').map(l => l.id);
    if (pendingIds.length > 0) {
      try {
        const result = await leadGenerationApi.startBatchEnrichment(allLeadIds);
        console.log(`[BatchEnrich] Triggered: ${result.toEnrich} to enrich, ${result.alreadyEnriched} already done`);
      } catch (err: any) {
        console.warn('Failed to trigger batch enrichment:', err.message);
      }
    }

    // Set initial progress
    const alreadyDone = leads.filter(l => l.enrichmentStatus === 'COMPLETED').length;
    const AVG_SECONDS_PER_LEAD = 40; // ~40s per lead with optimized prompts
    const remaining = leads.length - alreadyDone;
    const estMin = Math.max(1, Math.ceil((remaining * AVG_SECONDS_PER_LEAD) / 60));
    setAutoEnrichProgress({ total: leads.length, done: alreadyDone, estimatedMinLeft: estMin, overEstimate: false });
    enrichStartTimeRef.current = Date.now();

    // Start polling for progress
    startEnrichmentPolling(allLeadIds, leads.length);
  };

  // Poll backend for enrichment progress every 5 seconds
  const startEnrichmentPolling = (leadIds: string[], totalLeads: number) => {
    const pollInterval = 5000;
    const maxPolls = 300; // 25 min max
    let pollCount = 0;
    const initialEstimateSec = totalLeads * 40;

    const poll = async () => {
      try {
        const progress = await leadGenerationApi.getEnrichmentProgress(leadIds);

        // Update leads in-place — replace with enriched data as it arrives
        setGeneratedLeads(prev => {
          const updatedMap = new Map(progress.leads.map((l: GeneratedLead) => [l.id, l]));
          return prev.map(l => updatedMap.get(l.id) || l);
        });

        // Update progress bar
        const elapsed = (Date.now() - enrichStartTimeRef.current) / 1000;
        const remaining = progress.pending + progress.inProgress;
        const doneCount = progress.completed + progress.failed;
        const avgPerLead = doneCount > 0 ? elapsed / doneCount : 40;
        const estMinLeft = Math.max(1, Math.ceil((remaining * avgPerLead) / 60));
        const isOverEstimate = elapsed > initialEstimateSec && remaining > 0;

        if (remaining > 0) {
          setAutoEnrichProgress({
            total: progress.total,
            done: progress.completed,
            estimatedMinLeft: estMinLeft,
            overEstimate: isOverEstimate,
          });
        }

        // Stop polling when all done
        if (progress.pending === 0 && progress.inProgress === 0) {
          setAutoEnrichProgress(null);
          if (progress.completed > 0) {
            toast.success(`Found ${progress.completed} leads with contacts!`);
          } else {
            toast.info('No leads with valid contacts found. Try a different search.');
          }
          return;
        }
      } catch (err) {
        console.warn('Enrichment poll failed:', err);
      }

      pollCount++;
      if (pollCount < maxPolls) {
        enrichPollingRef.current = setTimeout(poll, pollInterval);
      } else {
        setAutoEnrichProgress(null);
        toast.info('Enrichment is still running in the background. Results will update automatically.');
      }
    };

    // First poll after 5 seconds
    enrichPollingRef.current = setTimeout(poll, pollInterval);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (enrichPollingRef.current) {
        clearTimeout(enrichPollingRef.current);
      }
    };
  }, []);

  const handleQuickAdd = async (lead: GeneratedLead) => {
    setPendingAddLead({ lead });
    setAttrDialogOpen(true);
  };

  const handleAddToTracking = async (result: LeadSearchResult) => {
    try {
      const leadData: AddResultToLeadsInput = {
        companyName: result.companyName,
        contactName: result.contactName || '',
        email: result.contactEmail || '',
        industry: result.industry,
        employeeCount: result.employeeCount,
        website: result.website,
        location: result.location,
        description: result.description,
        dataSource: result.dataSource,
        potentialRating: result.potentialRating,
        aiScore: result.aiScore,
        potentialValue: result.potentialValue,
        aiRecommendation: result.aiRecommendation,
        tags: result.tags
      };

      await aiCrmApi.leadGeneration.addResultToLeads(result.id, leadData);
      toast.success(`"${result.companyName}" added to lead tracking successfully!`);
      setSearchResults(prev => prev.filter(r => r.id !== result.id));
    } catch (error: any) {
      console.error('Error adding lead:', error);
      if (error.response?.status === 409) {
        toast.error(`A lead with email "${result.contactEmail}" already exists`);
      } else {
        toast.error(error.response?.data?.message || 'Failed to add lead to tracking');
      }
    }
  };

  const handleScheduleDemo = (result: LeadSearchResult) => {
    setScheduleDemoLead(result);
    setSelectedTemplate(demoEmailTemplates[0].id);
    setDemoDate(new Date(Date.now() + 24 * 60 * 60 * 1000));
    setAdditionalNotes('');
    setScheduleDemoOpen(true);
  };

  const handleSendDemoInvite = async () => {
    if (!scheduleDemoLead || !demoDate) {
      toast.error('Please select a date for the demo');
      return;
    }

    setIsSendingDemo(true);
    try {
      const template = demoEmailTemplates.find(t => t.id === selectedTemplate);

      // Replace template variables
      const subject = template?.subject
        .replace(/{{companyName}}/g, scheduleDemoLead.companyName)
        .replace(/{{industry}}/g, scheduleDemoLead.industry || 'your');

      const content = template?.content
        .replace(/{{contactName}}/g, scheduleDemoLead.contactName || 'there')
        .replace(/{{companyName}}/g, scheduleDemoLead.companyName)
        .replace(/{{industry}}/g, scheduleDemoLead.industry || 'your')
        .replace(/{{senderName}}/g, 'Your Sales Team');

      // Simulate sending (in production, this would call an API)
      await new Promise(resolve => setTimeout(resolve, 1500));

      toast.success(`Demo invitation sent to ${scheduleDemoLead.companyName}!`);
      console.log('Demo scheduled:', {
        lead: scheduleDemoLead,
        template: selectedTemplate,
        date: demoDate,
        subject,
        content,
        notes: additionalNotes
      });

      setScheduleDemoOpen(false);
      setScheduleDemoLead(null);
    } catch (error) {
      console.error('Error scheduling demo:', error);
      toast.error('Failed to send demo invitation');
    } finally {
      setIsSendingDemo(false);
    }
  };

  const getPreviewContent = () => {
    if (!scheduleDemoLead) return '';
    const template = demoEmailTemplates.find(t => t.id === selectedTemplate);
    return template?.content
      .replace(/{{contactName}}/g, scheduleDemoLead.contactName || 'there')
      .replace(/{{companyName}}/g, scheduleDemoLead.companyName)
      .replace(/{{industry}}/g, scheduleDemoLead.industry || 'your')
      .replace(/{{senderName}}/g, 'Your Sales Team') || '';
  };

  const handleSelectAll = () => {
    if (selectedResults.length === searchResults.length) {
      setSelectedResults([]);
    } else {
      setSelectedResults(searchResults.map(r => r.id));
    }
  };

  const handleBulkAdd = async () => {
    if (selectedResults.length === 0) {
      toast.warning('Please select at least one lead');
      return;
    }

    try {
      const leadsToAdd = searchResults.filter(r => selectedResults.includes(r.id));
      const bulkData: AddResultToLeadsInput[] = leadsToAdd.map(result => ({
        companyName: result.companyName,
        contactName: result.contactName || '',
        email: result.contactEmail || '',
        industry: result.industry,
        employeeCount: result.employeeCount,
        website: result.website,
        location: result.location,
        description: result.description,
        dataSource: result.dataSource,
        potentialRating: result.potentialRating,
        aiScore: result.aiScore,
        potentialValue: result.potentialValue,
        aiRecommendation: result.aiRecommendation,
        tags: result.tags
      }));

      const response = await aiCrmApi.leadGeneration.bulkAddResults({ results: bulkData });

      if (response.failed > 0) {
        toast.warning(
          `Added ${response.created} leads successfully. ${response.failed} failed (duplicates or errors).`
        );
      } else {
        toast.success(`Successfully added ${response.created} leads to tracking!`);
      }

      const addedEmails = response.leads.map((lead: any) => lead.email);
      setSearchResults(prev => prev.filter(r => !addedEmails.includes(r.contactEmail)));
      setSelectedResults([]);
    } catch (error: any) {
      console.error('Error bulk adding leads:', error);
      toast.error(error.response?.data?.message || 'Failed to add leads to tracking');
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearPrompt = () => {
    setSearchPrompt('');
    setFilterTitle('');
    setFilterIndustry('');
    setFilterLocation('');
    setFilterTrigger('');
  };

  // Paginate results
  const paginatedResults = searchResults.slice(
    (page - 1) * resultsPerPage,
    page * resultsPerPage
  );

  // Get source icon
  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'linkedin': return <LinkedInIcon sx={{ fontSize: 16, color: '#0077b5' }} />;
      case 'business-directory': return <BusinessIcon sx={{ fontSize: 16, color: '#ff9800' }} />;
      case 'industry-website': return <WebIcon sx={{ fontSize: 16, color: '#4caf50' }} />;
      default: return <SearchIcon sx={{ fontSize: 16 }} />;
    }
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      {/* Header - AI Image Generator Style */}
      <Box sx={{ mb: isMobile ? 2 : 4, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isMobile ? 1 : 1.5, mb: isMobile ? 1 : 2 }}>
          <AIIcon sx={{ fontSize: isMobile ? 28 : 40, color: 'primary.main' }} />
          <Typography variant={isMobile ? 'h6' : 'h4'} sx={{ fontWeight: 700 }}>
            AI Lead Generation
          </Typography>
        </Box>
        <Typography variant={isMobile ? 'body2' : 'h6'} color="text.secondary" sx={{ fontWeight: 400 }}>
          What kind of leads are you looking for today?
        </Typography>
      </Box>

      {/* Main Prompt Card - Clean and Simple */}
      <Card sx={{ mb: isMobile ? 2 : 3, maxWidth: 800, mx: 'auto' }} id="tour-lead-search">
        <CardContent sx={{ p: isMobile ? 2 : 4 }}>
          {/* Inline Prompt Builder */}
          <Box sx={{
            p: isMobile ? 2 : 3,
            borderRadius: 3,
            bgcolor: 'grey.50',
            border: '2px solid',
            borderColor: (filterTitle && filterIndustry && filterLocation) ? 'primary.main' : 'grey.200',
            transition: 'border-color 0.3s',
            mb: isMobile ? 2 : 3,
          }}>
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 0.75,
              lineHeight: 2.5,
              fontSize: isMobile ? '0.95rem' : '1.1rem',
              color: 'text.primary',
            }}>
              <Typography component="span" sx={{ fontWeight: 600, fontSize: 'inherit', color: 'text.secondary' }}>
                Find
              </Typography>
              <Autocomplete
                freeSolo
                options={[
                  'CEO', 'CTO', 'CFO', 'COO', 'CMO',
                  'VP Sales', 'VP Marketing', 'VP Operations',
                  'Head of Sales', 'Head of Marketing', 'Head of IT', 'Head of Digital',
                  'Director', 'Managing Director', 'General Manager',
                  'Sales Manager', 'Marketing Manager', 'Business Development',
                  'HR Manager', 'Finance Manager',
                ]}
                inputValue={filterTitle}
                onInputChange={(_e, val) => handleFilterChange('title', val)}
                disableClearable
                sx={{ display: 'inline-flex', minWidth: isMobile ? 120 : 160, maxWidth: 220 }}
                PopperComponent={(props) => <Popper {...props} sx={{ '& .MuiAutocomplete-listbox': { fontSize: '0.85rem', maxHeight: 200 } }} />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    placeholder="Title / Department"
                    sx={{
                      '& .MuiInput-root': {
                        fontSize: 'inherit', fontWeight: 600, color: 'primary.main',
                        '&:before': { borderBottom: '2px dashed', borderColor: filterTitle ? 'primary.main' : 'grey.400' },
                        '&:hover:before': { borderBottom: '2px solid', borderColor: 'primary.main' },
                        '&:after': { borderColor: 'primary.main' },
                      },
                      '& input::placeholder': { color: 'grey.400', opacity: 1, fontWeight: 500 },
                    }}
                  />
                )}
              />
              <Typography component="span" sx={{ fontWeight: 600, fontSize: 'inherit', color: 'text.secondary' }}>
                of
              </Typography>
              <Autocomplete
                freeSolo
                options={industrySuggestions}
                inputValue={filterIndustry}
                onInputChange={(_e, val) => handleFilterChange('industry', val)}
                disableClearable
                sx={{ display: 'inline-flex', minWidth: isMobile ? 120 : 160, maxWidth: 220 }}
                PopperComponent={(props) => <Popper {...props} sx={{ '& .MuiAutocomplete-listbox': { fontSize: '0.85rem', maxHeight: 200 } }} />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    placeholder="Industry / Sector"
                    sx={{
                      '& .MuiInput-root': {
                        fontSize: 'inherit', fontWeight: 600, color: 'primary.main',
                        '&:before': { borderBottom: '2px dashed', borderColor: filterIndustry ? 'primary.main' : 'grey.400' },
                        '&:hover:before': { borderBottom: '2px solid', borderColor: 'primary.main' },
                        '&:after': { borderColor: 'primary.main' },
                      },
                      '& input::placeholder': { color: 'grey.400', opacity: 1, fontWeight: 500 },
                    }}
                  />
                )}
              />
              <Typography component="span" sx={{ fontWeight: 600, fontSize: 'inherit', color: 'text.secondary' }}>
                located in
              </Typography>
              <Autocomplete
                freeSolo
                options={locationSuggestions}
                inputValue={filterLocation}
                onInputChange={(_e, val) => handleFilterChange('location', val)}
                disableClearable
                sx={{ display: 'inline-flex', minWidth: isMobile ? 120 : 160, maxWidth: 220 }}
                PopperComponent={(props) => <Popper {...props} sx={{ '& .MuiAutocomplete-listbox': { fontSize: '0.85rem', maxHeight: 200 } }} />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    placeholder="Location"
                    sx={{
                      '& .MuiInput-root': {
                        fontSize: 'inherit', fontWeight: 600, color: 'primary.main',
                        '&:before': { borderBottom: '2px dashed', borderColor: filterLocation ? 'primary.main' : 'grey.400' },
                        '&:hover:before': { borderBottom: '2px solid', borderColor: 'primary.main' },
                        '&:after': { borderColor: 'primary.main' },
                      },
                      '& input::placeholder': { color: 'grey.400', opacity: 1, fontWeight: 500 },
                    }}
                  />
                )}
              />
            </Box>
            {/* Optional trigger field */}
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 0.75,
              lineHeight: 2.5,
              fontSize: isMobile ? '0.85rem' : '0.95rem',
              color: 'text.secondary',
              mt: 1.5,
            }}>
              <Typography component="span" sx={{ fontWeight: 500, fontSize: 'inherit', color: 'text.disabled', fontStyle: 'italic' }}>
                (Optional to improve search result)
              </Typography>
              <Autocomplete
                freeSolo
                options={triggerSuggestions}
                inputValue={filterTrigger}
                onInputChange={(_e, val) => handleFilterChange('trigger', val)}
                disableClearable
                sx={{ display: 'inline-flex', minWidth: isMobile ? 140 : 180, maxWidth: 280 }}
                PopperComponent={(props) => <Popper {...props} sx={{ '& .MuiAutocomplete-listbox': { fontSize: '0.85rem', maxHeight: 200 } }} />}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    placeholder="e.g. raised funding, hiring, expanding"
                    sx={{
                      '& .MuiInput-root': {
                        fontSize: 'inherit', fontWeight: 600,
                        color: filterTrigger ? 'primary.main' : 'text.disabled',
                        '&:before': { borderBottom: '2px dashed', borderColor: filterTrigger ? 'primary.main' : 'grey.300' },
                        '&:hover:before': { borderBottom: '2px solid', borderColor: 'primary.main' },
                        '&:after': { borderColor: 'primary.main' },
                      },
                      '& input::placeholder': { color: 'grey.300', opacity: 1, fontWeight: 400, fontStyle: 'italic' },
                    }}
                  />
                )}
              />
            </Box>
          </Box>

          {/* AI Suggested Quick-fill Presets */}
          {(hintsLoading || (hintsData?.advice?.lead_generation && hintsData.advice.lead_generation.length > 0)) && (
            <Box sx={{ mb: isMobile ? 2 : 3 }}>
              {hintsLoading ? (
                <>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: isMobile ? 1.5 : 2, py: isMobile ? 1 : 1.25, borderRadius: 2,
                    bgcolor: 'rgba(25, 118, 210, 0.03)',
                    border: '1px solid rgba(25, 118, 210, 0.08)',
                    mb: 1.5,
                  }}>
                    <AIIcon sx={{ fontSize: 18, color: 'grey.400' }} />
                    <Box sx={{ flex: 1 }}><ShimmerLine /></Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    <ShimmerChip width={200} />
                    <ShimmerChip width={240} />
                  </Box>
                </>
              ) : (
                <>
              {hintsData?.leadGenerationInsight && (
                <Box sx={{
                  display: 'flex', alignItems: 'flex-start', gap: 1,
                  px: isMobile ? 1.5 : 2, py: isMobile ? 1 : 1.25, borderRadius: 2,
                  bgcolor: 'rgba(25, 118, 210, 0.06)',
                  border: '1px solid',
                  borderColor: 'rgba(25, 118, 210, 0.15)',
                  mb: 1.5,
                }}>
                  <AIIcon sx={{ fontSize: 18, color: 'primary.main', mt: 0.25, flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    {hintsData.leadGenerationInsight}
                  </Typography>
                </Box>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500, fontSize: isMobile ? '0.75rem' : undefined }}>
                Quick-fill suggestions based on your company:
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {hintsData?.advice?.lead_generation?.map((hint: any, idx: number) => {
                  // Support both structured objects (new) and plain strings (legacy)
                  const isStructured = typeof hint === 'object' && hint !== null;
                  const label = isStructured ? (hint.label || `${hint.industry} in ${hint.location}`) : String(hint);

                  return (
                    <Chip
                      key={idx}
                      label={label}
                      size="small"
                      onClick={() => {
                        if (isStructured) {
                          setFilterTitle(hint.title || '');
                          setFilterIndustry(hint.industry || '');
                          setFilterLocation(hint.location || '');
                          setFilterTrigger(hint.trigger || '');
                          setSearchPrompt(buildPromptFromFilters(hint.title || '', hint.industry || '', hint.location || '', hint.trigger || ''));
                        } else {
                          // Legacy: just set as raw prompt
                          setSearchPrompt(String(hint));
                        }
                      }}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: 'rgba(25, 118, 210, 0.06)',
                        borderColor: 'rgba(25, 118, 210, 0.25)',
                        color: 'text.primary',
                        fontSize: '0.75rem',
                        height: 'auto',
                        '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 },
                        '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.14)' },
                      }}
                      variant="outlined"
                      icon={<AIIcon sx={{ fontSize: 14 }} />}
                    />
                  );
                })}
              </Box>
                </>
              )}
            </Box>
          )}



          {/* Similar search found banner */}
          {/* Similar search alert removed — each search is individual */}

          {/* Action Button */}
          <Box sx={{ display: 'flex', gap: isMobile ? 1 : 2, justifyContent: 'center', flexDirection: isMobile ? 'column' : 'row' }}>
            {(isSearching || isPollingAI) ? (
              <Button
                variant="contained"
                color="error"
                size={isMobile ? 'medium' : 'large'}
                startIcon={<StopIcon />}
                onClick={handleStopSearch}
                fullWidth={isMobile}
                sx={{
                  minWidth: isMobile ? 'auto' : 200,
                  py: isMobile ? 1.2 : 1.5,
                  px: isMobile ? 3 : 4,
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  borderRadius: 3,
                  boxShadow: 3,
                  '&:hover': { boxShadow: 6 },
                }}
              >
                Stop Search
              </Button>
            ) : (
              <Button
                variant="contained"
                size={isMobile ? 'medium' : 'large'}
                startIcon={<AIIcon />}
                onClick={handleDatabaseSearch}
                disabled={!filterTitle.trim() || !filterIndustry.trim() || !filterLocation.trim()}
                fullWidth={isMobile}
                sx={{
                  minWidth: isMobile ? 'auto' : 200,
                  py: isMobile ? 1.2 : 1.5,
                  px: isMobile ? 3 : 4,
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  borderRadius: 3,
                  boxShadow: 3,
                  '&:hover': { boxShadow: 6 },
                }}
              >
                Generate Leads
              </Button>
            )}
            {(filterTitle || filterIndustry || filterLocation || filterTrigger) && (
              <Button
                variant="text"
                color="inherit"
                onClick={handleClearPrompt}
                sx={{ color: 'text.secondary' }}
                size={isMobile ? 'small' : 'medium'}
              >
                Clear
              </Button>
            )}
          </Box>

        </CardContent>
      </Card>

      {/* AI Selected Sources Info */}
      {aiSelectedSources.length > 0 && (
        <Card sx={{ mb: isMobile ? 2 : 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
          <CardContent sx={{ py: isMobile ? 1.5 : 2, px: isMobile ? 2 : 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AIIcon sx={{ color: 'primary.main', fontSize: isMobile ? 20 : 24 }} />
              <Typography variant={isMobile ? 'body2' : 'subtitle1'} sx={{ fontWeight: 600, color: 'primary.main' }}>
                AI Selected Sources
              </Typography>
            </Box>
            <Box sx={{
              display: 'flex',
              flexWrap: isMobile ? 'nowrap' : 'wrap',
              gap: isMobile ? 1 : 2,
              overflowX: isMobile ? 'auto' : 'visible',
              pb: isMobile ? 0.5 : 0,
              mx: isMobile ? -2 : 0,
              px: isMobile ? 2 : 0,
              WebkitOverflowScrolling: 'touch',
              '&::-webkit-scrollbar': { display: 'none' }
            }}>
              {aiSelectedSources.map((source, index) => (
                <Paper key={index} sx={{ p: isMobile ? 1 : 1.5, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  {getSourceIcon(source.source)}
                  <Box>
                    <Typography variant={isMobile ? 'caption' : 'body2'} sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                      {source.source.replace('-', ' ')}
                    </Typography>
                    {!isMobile && (
                      <Typography variant="caption" color="text.secondary">
                        {source.reason} ({source.expectedResults} expected)
                      </Typography>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Clarification Questions Panel */}
      {clarificationState.isAskingQuestions && (
        <Card sx={{ mb: isMobile ? 2 : 3, maxWidth: 800, mx: 'auto', bgcolor: 'warning.50', border: '2px solid', borderColor: 'warning.main' }}>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: isMobile ? 1.5 : 2 }}>
              <AIIcon sx={{ color: 'warning.dark', fontSize: isMobile ? 20 : 24 }} />
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600, color: 'warning.dark' }}>
                AI Needs More Information
              </Typography>
            </Box>
            <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary" sx={{ mb: isMobile ? 2 : 3 }}>
              To find the most relevant leads, please answer these questions:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1.5 : 2 }}>
              {clarificationState.questions.map((question, idx) => (
                <Box key={idx}>
                  <Typography variant={isMobile ? 'caption' : 'body2'} sx={{ fontWeight: 500, mb: 0.5 }}>
                    {idx + 1}. {question}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Your answer..."
                    value={clarificationState.answers[idx] || ''}
                    onChange={(e) => {
                      const newAnswers = [...clarificationState.answers];
                      newAnswers[idx] = e.target.value;
                      setClarificationState({
                        ...clarificationState,
                        answers: newAnswers,
                      });
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        fontSize: isMobile ? '0.875rem' : undefined
                      }
                    }}
                  />
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: isMobile ? 1 : 2, mt: isMobile ? 2 : 3, justifyContent: isMobile ? 'stretch' : 'flex-end', flexDirection: isMobile ? 'column-reverse' : 'row' }}>
              <Button
                variant="outlined"
                onClick={handleCancelClarification}
                sx={{ borderColor: 'grey.400', color: 'text.secondary' }}
                size={isMobile ? 'medium' : 'medium'}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="warning"
                startIcon={<SearchIcon />}
                onClick={() => handleAnswerQuestions(clarificationState.answers)}
                disabled={clarificationState.answers.some((a) => !a?.trim()) || clarificationState.answers.length < clarificationState.questions.length}
                size={isMobile ? 'medium' : 'medium'}
              >
                {isMobile ? 'Search' : 'Search with Answers'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* AI Progress Indicator - show when searching, polling, or enriching */}
      {(isSearching || isPollingAI || autoEnrichProgress) && (
        <Card sx={{ maxWidth: 600, mx: 'auto' }}>
          <CardContent sx={{ py: isMobile ? 3 : 4 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {/* AI Icon with pulse animation */}
              <Box
                sx={{
                  position: 'relative',
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AIIcon
                  sx={{
                    fontSize: isMobile ? 40 : 56,
                    color: 'primary.main',
                    animation: 'pulse 2s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                      '50%': { opacity: 0.7, transform: 'scale(1.1)' },
                    },
                  }}
                />
              </Box>

              {/* Progress Text */}
              <Typography
                variant={isMobile ? 'subtitle1' : 'h6'}
                sx={{
                  mb: 1,
                  fontWeight: 600,
                  color: 'text.primary',
                  minHeight: 28,
                }}
              >
                {autoEnrichProgress
                  ? `Finding contacts for ${autoEnrichProgress.total} leads... (${autoEnrichProgress.done} done)`
                  : aiProgressText || 'Initializing AI search...'}
              </Typography>

              {/* Progress Bar */}
              <Box sx={{ width: '100%', mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={autoEnrichProgress
                    ? (autoEnrichProgress.done / autoEnrichProgress.total) * 100
                    : aiProgress}
                  sx={{
                    height: isMobile ? 8 : 10,
                    borderRadius: 5,
                    backgroundColor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1976d2 100%)',
                      backgroundSize: '200% 100%',
                      animation: 'shimmer 2s linear infinite',
                      '@keyframes shimmer': {
                        '0%': { backgroundPosition: '200% 0' },
                        '100%': { backgroundPosition: '-200% 0' },
                      },
                    },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {autoEnrichProgress
                      ? `${Math.round((autoEnrichProgress.done / autoEnrichProgress.total) * 100)}%`
                      : `${Math.round(aiProgress)}%`}
                  </Typography>
                </Box>
              </Box>

              {/* Helper Text */}
              <Typography
                variant={isMobile ? 'caption' : 'body2'}
                color="text.disabled"
                textAlign="center"
                sx={{ px: isMobile ? 2 : 0 }}
              >
                {autoEnrichProgress
                  ? autoEnrichProgress.overEstimate
                    ? 'Taking longer than expected, please wait patiently'
                    : autoEnrichProgress.estimatedMinLeft > 0
                      ? `Estimated ~${autoEnrichProgress.estimatedMinLeft} min remaining`
                      : 'Almost done...'
                  : isMobile
                    ? 'Finding the best leads for you...'
                    : 'AI is searching multiple sources and analyzing companies to find the best leads for you'}
              </Typography>

              {/* Reassurance: typical wait + safe-to-leave hint */}
              {!autoEnrichProgress && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  textAlign="center"
                  sx={{ mt: 1.5, px: isMobile ? 2 : 0, display: 'block' }}
                >
                  Typical search takes 5-10 minutes. You can close this tab — your results will be saved automatically{notificationPermission === 'granted' ? ", and we'll notify you when they're ready" : ''}.
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Slow-search banner — shown during an in-progress search that's taking longer than usual */}
      {slowSearchBanner.show && (isSearching || isPollingAI) && (
        <Card sx={{ maxWidth: 600, mx: 'auto', mb: 2, border: '1px solid', borderColor: 'warning.main', bgcolor: 'warning.50' }}>
          <CardContent sx={{ py: isMobile ? 2 : 2.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                ⏳ This search is taking longer than usual
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You can keep waiting — we'll keep trying. Or switch to one of these searches that usually work faster:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {slowSearchBanner.suggestions.map((sug, index) => (
                  <Chip
                    key={index}
                    label={`${sug.title} of ${sug.industry} in ${sug.location}`}
                    onClick={() => {
                      setFilterTitle(sug.title);
                      setFilterIndustry(sug.industry);
                      setFilterLocation(sug.location);
                      setSearchPrompt(buildPromptFromFilters(sug.title, sug.industry, sug.location, ''));
                      setSlowSearchBanner({ show: false, dismissed: true, suggestions: [] });
                      // Cancel current search and let user re-click Search with new query
                      localStorage.removeItem(PIPELINE_JOB_KEY);
                      setAiProgress(0);
                      setIsSearching(false);
                      setIsPollingAI(false);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    sx={{
                      cursor: 'pointer',
                      height: 'auto',
                      py: 0.75,
                      justifyContent: 'flex-start',
                      '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'left' },
                      '&:hover': { bgcolor: 'warning.100' },
                    }}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setSlowSearchBanner({ show: false, dismissed: true, suggestions: [] })}
                >
                  Keep waiting
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* No Leads Found - Suggestions Panel */}
      {noLeadsState.show && !isSearching && !isPollingAI && (
        <Card sx={{ maxWidth: 600, mx: 'auto', mb: 3, border: '1px solid', borderColor: 'warning.main', bgcolor: 'warning.50' }}>
          <CardContent sx={{ py: isMobile ? 2.5 : 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              {/* Icon */}
              <Box
                sx={{
                  width: isMobile ? 48 : 56,
                  height: isMobile ? 48 : 56,
                  borderRadius: '50%',
                  bgcolor: 'warning.100',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <AIIcon sx={{ fontSize: isMobile ? 24 : 28, color: 'warning.main' }} />
              </Box>

              {/* Title */}
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600, mb: 1 }}>
                {noLeadsState.reason === 'timeout'
                  ? "This one's on us"
                  : 'No leads found for your search'}
              </Typography>

              {/* Search prompt reminder */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {noLeadsState.reason === 'timeout'
                  ? `Our AI couldn't complete "${noLeadsState.searchPrompt}" in time. Try one of the alternatives below.`
                  : `Search: "${noLeadsState.searchPrompt}"`}
              </Typography>

              {/* AI-generated alternatives (takes priority when available) */}
              {noLeadsState.aiSuggestedQueries && noLeadsState.aiSuggestedQueries.length > 0 && (
                <Box sx={{ width: '100%', textAlign: 'left', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
                    ✨ Try one of these instead:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {noLeadsState.aiSuggestedQueries.map((sug, index) => (
                      <Chip
                        key={index}
                        label={`${sug.title} of ${sug.industry} in ${sug.location}`}
                        onClick={() => {
                          setFilterTitle(sug.title);
                          setFilterIndustry(sug.industry);
                          setFilterLocation(sug.location);
                          setSearchPrompt(buildPromptFromFilters(sug.title, sug.industry, sug.location, ''));
                          setNoLeadsState({ show: false, searchPrompt: '', suggestions: [] });
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        sx={{
                          cursor: 'pointer',
                          height: 'auto',
                          py: 0.75,
                          justifyContent: 'flex-start',
                          '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'left' },
                          '&:hover': { bgcolor: 'primary.100' },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Generic suggestions (always shown as fallback / complement) */}
              <Box sx={{ width: '100%', textAlign: 'left', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
                  💡 Try these suggestions:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {noLeadsState.suggestions.map((suggestion, index) => (
                    <Box component="li" key={index} sx={{ mb: 0.75 }}>
                      <Typography variant="body2" color="text.secondary">
                        {suggestion}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Example prompts */}
              <Box sx={{ width: '100%', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
                  📝 Example searches that work well:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                  {[
                    'Technology companies in Hong Kong',
                    'Restaurants in Singapore with delivery',
                    'Retail stores in Asia Pacific',
                    'Manufacturing companies in China'
                  ].map((example) => (
                    <Chip
                      key={example}
                      label={example}
                      size="small"
                      onClick={() => {
                        setSearchPrompt(example);
                        const parsed = parsePromptIntoFilters(example);
                        setFilterTitle(parsed.title || '');
                        setFilterIndustry(parsed.industry || '');
                        setFilterLocation(parsed.location || extractLocation(example));
                        setNoLeadsState({ show: false, searchPrompt: '', suggestions: [] });
                      }}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'primary.100' }
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Action buttons */}
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setSearchPrompt(noLeadsState.searchPrompt);
                    setNoLeadsState({ show: false, searchPrompt: '', suggestions: [] });
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  Edit my search
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setNoLeadsState({ show: false, searchPrompt: '', suggestions: [] })}
                >
                  Dismiss
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Empty State - only show when NOT searching and NOT polling and NOT enriching and no suggestions shown */}
      {!isSearching && !isPollingAI && !autoEnrichProgress && !noLeadsState.show && searchResults.length === 0 && generatedLeads.length === 0 && (
        <Box sx={{ maxWidth: 600, mx: 'auto', textAlign: 'center', py: isMobile ? 3 : 4, px: isMobile ? 2 : 0 }}>
          <AIIcon sx={{ fontSize: isMobile ? 48 : 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="text.secondary" sx={{ mb: 1 }}>
            Describe your ideal leads above
          </Typography>
          <Typography variant={isMobile ? 'caption' : 'body2'} color="text.disabled">
            {isMobile ? 'AI will find the best matching companies' : 'Our AI will search multiple sources and find the best matching companies for you'}
          </Typography>
        </Box>
      )}

      {/* Compact View Results (Generated Leads from Database) */}
      {generatedLeads.length > 0 && (
        <Box>
          {/* Results Header */}
          <Box sx={{ mb: isMobile ? 1.5 : 2, display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600 }}>
              Search Results
            </Typography>
            <Typography variant="body2" color="text.secondary">
              — {generatedLeads.length} {generatedLeads.length === 1 ? 'lead' : 'leads'} found
            </Typography>
          </Box>

          {/* Compact Cards - Full width on mobile */}
          <Grid container spacing={isMobile ? 1.5 : 2}>
            {generatedLeads
              .slice((page - 1) * resultsPerPage, page * resultsPerPage)
              .map((lead) => (
                <Grid item xs={12} md={6} key={lead.id}>
                  <GeneratedLeadCard
                    lead={lead}
                    onViewDetails={handleViewDetails}
                    onQuickAdd={lead.contactEmail ? handleQuickAdd : undefined}
                    onRetryEnrich={handleRetryEnrich}
                  />
                </Grid>
              ))}
          </Grid>

          {/* Pagination */}
          {generatedLeads.length > resultsPerPage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: isMobile ? 2 : 3 }}>
              <Pagination
                count={Math.ceil(generatedLeads.length / resultsPerPage)}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? 'small' : 'large'}
                showFirstButton={!isMobile}
                showLastButton={!isMobile}
              />
            </Box>
          )}

          {/* Generate More Leads */}
          {!isSearching && !isPollingAI && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<AIIcon />}
                onClick={() => handleGenerateMore()}
                disabled={isSearching || isPollingAI}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Generate 10 More Leads
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Schedule Demo Dialog */}
      <Dialog
        open={scheduleDemoOpen}
        onClose={() => setScheduleDemoOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ py: isMobile ? 1.5 : 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <EventIcon color="primary" sx={{ fontSize: isMobile ? 20 : 24 }} />
            <Box>
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600 }}>
                {isMobile ? 'Schedule Demo' : `Schedule Demo for ${scheduleDemoLead?.companyName}`}
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary">
                {isMobile ? scheduleDemoLead?.companyName : 'Send a demo invitation email with pre-selected template'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: isMobile ? 2 : 3 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={isMobile ? 2 : 3}>
              {/* Lead Info */}
              <Grid item xs={12}>
                <Paper sx={{ p: isMobile ? 1.5 : 2, bgcolor: 'grey.50' }}>
                  <Grid container spacing={isMobile ? 1 : 2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Company</Typography>
                      <Typography variant={isMobile ? 'caption' : 'body2'} fontWeight="bold" sx={{ display: 'block' }}>{scheduleDemoLead?.companyName}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Contact</Typography>
                      <Typography variant={isMobile ? 'caption' : 'body2'} sx={{ display: 'block' }}>{scheduleDemoLead?.contactName || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                      <Typography variant={isMobile ? 'caption' : 'body2'} sx={{ display: 'block', wordBreak: 'break-all' }}>{scheduleDemoLead?.contactEmail || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Industry</Typography>
                      <Typography variant={isMobile ? 'caption' : 'body2'} sx={{ display: 'block' }}>{scheduleDemoLead?.industry || 'N/A'}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Demo Date */}
              <Grid item xs={12} sm={6}>
                <DateTimePicker
                  label="Proposed Demo Date & Time"
                  value={demoDate}
                  onChange={(newValue) => setDemoDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      size: isMobile ? 'small' : 'medium'
                    }
                  }}
                  minDate={new Date()}
                />
              </Grid>

              {/* Email Template Selection */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                  <InputLabel>Email Template</InputLabel>
                  <Select
                    value={selectedTemplate}
                    label="Email Template"
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    startAdornment={<EmailIcon sx={{ mr: 1, color: 'action.active', fontSize: isMobile ? 18 : 20 }} />}
                  >
                    {demoEmailTemplates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        <Typography variant={isMobile ? 'body2' : 'body1'}>{template.name}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Additional Notes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Additional Notes (Optional)"
                  multiline
                  rows={isMobile ? 2 : 2}
                  value={additionalNotes}
                  onChange={(e) => setAdditionalNotes(e.target.value)}
                  placeholder="Add any specific points you want to discuss..."
                  size={isMobile ? 'small' : 'medium'}
                />
              </Grid>

              {/* Email Preview */}
              <Grid item xs={12}>
                <Typography variant={isMobile ? 'body2' : 'subtitle2'} gutterBottom sx={{ fontWeight: 500 }}>
                  Email Preview
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: isMobile ? 1.5 : 2,
                    maxHeight: isMobile ? 180 : 250,
                    overflow: 'auto',
                    bgcolor: 'background.default'
                  }}
                >
                  <Typography variant={isMobile ? 'caption' : 'subtitle2'} color="primary" gutterBottom sx={{ fontWeight: 500 }}>
                    Subject: {demoEmailTemplates.find(t => t.id === selectedTemplate)?.subject
                      .replace(/{{companyName}}/g, scheduleDemoLead?.companyName || '')
                      .replace(/{{industry}}/g, scheduleDemoLead?.industry || 'your')}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography
                    variant={isMobile ? 'caption' : 'body2'}
                    sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}
                  >
                    {getPreviewContent()}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ p: isMobile ? 2 : 2, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
          <Button
            onClick={() => setScheduleDemoOpen(false)}
            fullWidth={isMobile}
            sx={{ order: isMobile ? 2 : 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={isSendingDemo ? <CircularProgress size={16} /> : <SendIcon />}
            onClick={handleSendDemoInvite}
            disabled={isSendingDemo || !demoDate}
            fullWidth={isMobile}
            sx={{ order: isMobile ? 1 : 2 }}
          >
            {isSendingDemo ? 'Sending...' : (isMobile ? 'Send Invitation' : 'Send Demo Invitation')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lead Detail Modal (for compact view) */}
      <LeadDetailModal
        open={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
        }}
        lead={selectedGeneratedLead}
        onAddToCRM={handleAddGeneratedLeadToCRM}
        onReject={handleRejectGeneratedLead}
      />

      {/* Previous Searches History */}
      {searchHistory.length > 0 && !isSearching && !isPollingAI && (
        <Box sx={{ mb: 3, maxWidth: 900, mx: 'auto' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
            Previous Searches
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {searchHistory.slice(0, 5).map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  py: 1.5,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  cursor: 'pointer',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'action.hover' },
                  transition: 'background-color 0.15s',
                  borderBottom: idx < Math.min(searchHistory.length, 5) - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
                onClick={() => loadHistorySearch(item)}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                    {item.searchPrompt}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(item.latestDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    {' · '}
                    {item.leadCount} {item.leadCount === 1 ? 'lead' : 'leads'}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 500,
                    flexShrink: 0,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  View
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Attribute Selection Dialog (shown before adding lead to CRM) */}
      <AttributeSelectionDialog
        open={attrDialogOpen}
        onClose={() => {
          setAttrDialogOpen(false);
          setPendingAddLead(null);
        }}
        onConfirm={handleConfirmAddToCRM}
        loading={attrDialogLoading}
        entityName={pendingAddLead?.lead.companyName}
        appliesTo="LEAD"
      />
    </Box>
  );
};

export default LeadGenerationTab;
